import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation, useNavigate } from "react-router-dom";
import { TbGripVertical, TbArrowBarToUp } from "react-icons/tb";


import styles from "./HomePageBuilder.module.css";
import confirmModalStyles from "./ConfirmModal.module.css";

import Chips from "../../component/chips/Chips";
import MediaLibraryModal from "../../component/mediaLibrary/MediaLibraryModal";
import { showToast } from "../../utils/Toastify/Toastify";
import { withContentRef } from "../../utils/contentRef";
import { truncateByLetters } from "../../utils/textTruncate";
import { useRbac } from "../../rbac/RbacContext";
import { useAppConfig } from "../../context/AppConfigContext";
import { useTenant } from "../../context/TenantContext";

import { MdDelete, MdOutlineTableRows, MdTableRows } from "react-icons/md";
import { IoEyeOffOutline } from "react-icons/io5";
import {
  IoAlbumsOutline,
   IoAddOutline,
  IoPencilOutline,
  IoTrashOutline,
  IoArrowBackOutline,
  IoArrowForwardOutline,
  IoAppsOutline,
  IoLayersOutline,
  IoNewspaperOutline,
  IoColorPaletteOutline,
  IoEyeOutline,
  IoClose,
  IoArrowUpOutline,
  IoArrowDownOutline,
  IoCopyOutline,
  IoDesktopOutline,
  IoTvOutline,
  IoPhonePortraitOutline,
  IoAddCircleOutline,
  IoRemoveCircleOutline,
  IoGridOutline,
  IoRadioButtonOnOutline,
  IoPieChartOutline,
  IoDiamondOutline,
  IoHomeOutline,
  IoArrowUndoOutline,
  IoArrowRedoOutline,
  IoHourglassOutline,
  IoSettingsOutline,
  IoSearchOutline,
  IoStarOutline,
} from "react-icons/io5";

import SectionModal from "./modals/SectionModal";
import ConfirmModal from "./ConfirmModal";
import TemplatePickerModal from "./modals/TemplatePickerModal";
import VersionHistoryModal from "./modals/VersionHistoryModal";
import {
  groupWidgetRows,
  RAIL_CLASS_KEY,
  LAYOUT_ASPECT_RATIO,
  BANNER_LAYOUT_TYPES,
  ALGORITHMIC_DATA_SOURCES,
  PLACEHOLDER_DATA_SOURCES,
  DEVICE_PRESETS,
  DATA_SOURCE_OPTIONS,
  IMAGE_RATIO_OPTIONS,
  TITLE_POSITION_OPTIONS,
  THEME_ACCENT_PRESETS,
  THEME_FONT_COLOR_PRESETS,
  THEME_COLOR_PALETTE,
  THEME_BACKGROUND_PRESETS,
  THEME_FOCUS_EFFECTS,
} from "./constants";

import {
  createCategories,
  createSections,
  deleteCategoriesns,
  deleteVideos,
  GenresList,
  GenresDeletebyId,
  getCategories,
  getSections,
  deleteSections,
  getSectionVersions,
  getSectionPendingChanges,
  restoreSectionVersion,
  postSwapOrderCategoyiesList,
  postSwapOrderVideoList,
  swapOrder,
  updateCategories,
  updateSections,
  getTheme,
  updateTheme,
  addCustomAccentColor,
  removeCustomAccentColor,
  addCustomBackground,
  removeCustomBackground,
  addCustomFontColor,
  removeCustomFontColor,
  getWidgetTypes,
  getAllContent,
} from "../../api/HttpApis";
import { AiOutlineTable } from "react-icons/ai";
import { BsCircleHalf, BsGrid3X3GapFill, BsSquare } from "react-icons/bs";

const RAIL_TABS = [
  { key: "widgets", label: "Widgets", icon: <AiOutlineTable/> },
  { key: "layers", label: "Layers", icon: <BsGrid3X3GapFill /> },
  { key: "theme", label: "Theme", icon: <BsCircleHalf /> },
  { key: "pages", label: "Pages", icon: <BsSquare /> },
];

// Bottom tab bar shown under the mobile canvas preview only -- mirrors the
// device's own in-app navigation (Home/Movies/Series/Live/My), not this
// builder's own pages, so it's a fixed static set rather than driven by
// `sections`.
const MOBILE_PREVIEW_NAV_ITEMS = [
  { key: "home", label: "Home", icon: <IoHomeOutline /> },
  { key: "movies", label: "Movies", icon: <MdOutlineTableRows /> },
  { key: "series", label: "Series", icon: <IoDiamondOutline /> },
  { key: "live", label: "Live", icon: <IoRadioButtonOnOutline /> },
  { key: "my", label: "My", icon: <IoStarOutline /> },
];

const DEFAULT_CARD_DESIGN = {
  imageRatio: "2:3",
  titlePosition: "below",
  dropShadow: true,
  showSubtitle: true,
  showLiveBadge: true,
};

const DEFAULT_BEHAVIOUR = {
  autoScroll: false,
  infiniteScroll: false,
  shuffleOrder: false,
  sorting: "manual",
};

// Shuffling a single item is a no-op. (Auto scroll has no fixed minimum: a
// rail needs it only when its cards overflow, which RailViewport measures,
// and a banner slider only when it has more than one slide.)
const SHUFFLE_MIN_ITEMS = 2;

// The behaviour that actually applies for a given item count. The saved flags
// are kept as-is (so they come back if items are added again); this just
// switches off whatever the current count doesn't allow.
function effectiveBehaviour(behaviour, itemCount = 0) {
  const b = { ...DEFAULT_BEHAVIOUR, ...behaviour };
  return {
    ...b,
    autoScroll: !!b.autoScroll,
    infiniteScroll: !!b.autoScroll && !!b.infiniteScroll,
    shuffleOrder: itemCount >= SHUFFLE_MIN_ITEMS && !!b.shuffleOrder,
  };
}

// Fewer cards than this and a rail can't auto-scroll or loop -- there isn't
// enough to move.
const SCROLL_BEHAVIOUR_MIN_ITEMS = 20;

// A rail with fewer than SCROLL_BEHAVIOUR_MIN_ITEMS cards can't auto-scroll or
// loop -- there isn't enough to move. Banners are exempt (they page one at a
// time). Kept here because the behaviour toggles below are disabled on it.
const isScrollBehaviourLocked = (layoutType, itemCount) =>
  !BANNER_LAYOUT_TYPES.includes(layoutType) && itemCount < SCROLL_BEHAVIOUR_MIN_ITEMS;

const SORTING_OPTIONS = [
  { value: "manual", label: "Manual order" },
  { value: "alphabetical", label: "Alphabetical (A-Z)" },
  { value: "newest", label: "Newest first" },
  { value: "oldest", label: "Oldest first" },
];

const DEFAULT_CATEGORY_CONFIG = {
  dataSource: "manual",
  cardDesign: DEFAULT_CARD_DESIGN,
  cardsVisible: { web: 6, tv: 5, mobile: 3 },
  behaviour: DEFAULT_BEHAVIOUR,
};

// The "Cards Visible Per Device" inputs only accept this range; anything
// typed outside it is clamped rather than silently accepted.
const CARDS_VISIBLE_MIN = 1;
const CARDS_VISIBLE_MAX = 50;

// Demoed on the first TV-device card only, so choosing a focus effect in the
// Theme Builder has *something* visible to show for it -- there's no real
// remote/keyboard focus interaction inside a canvas preview.
const TV_FOCUS_CLASS = {
  "glow-ring": styles.tvFocusGlow,
  scale: styles.tvFocusScale,
  lift: styles.tvFocusLift,
};

function ratioToCss(ratio) {
  return (ratio || "2:3").replace(":", " / ");
}

// The Section Inspector's "Image ratio" control is the source of truth for
// every layout type -- LAYOUT_ASPECT_RATIO is only consulted as a fallback
// for a category saved before cardDesign.imageRatio existed. Circle avatars
// and Square cards are always 1:1 regardless of what's saved -- their shape
// *is* the widget (see the disabled "Image ratio" select). Square needs this
// too because new sections were seeded with DEFAULT_CARD_DESIGN's "2:3",
// which saved as an explicit ratio and made Square render as portrait.
const FIXED_RATIO_LAYOUTS = {
  circle: { ratio: "1:1", reason: "Circle avatars are always 1:1" },
  square: { ratio: "1:1", reason: "Square cards are always 1:1" },
};

function getEffectiveImageRatio(layoutType, cardDesign) {
  if (FIXED_RATIO_LAYOUTS[layoutType]) return FIXED_RATIO_LAYOUTS[layoutType].ratio;
  return cardDesign?.imageRatio || LAYOUT_ASPECT_RATIO[layoutType];
}

// `<input type="datetime-local">`'s value/min are in the *local* wall-clock
// timezone, not UTC -- `date.toISOString()` would let an admin east of UTC
// pick a time that's already passed locally (e.g. IST is UTC+5:30, so "now"
// in UTC still looks 5.5 hours in the future to the input).
function toLocalDateTimeInputValue(date) {
  const localMs = date.getTime() - date.getTimezoneOffset() * 60000;
  return new Date(localMs).toISOString().slice(0, 19);
}

// Splits a "YYYY-MM-DDTHH:mm:ss" (24h, same shape toLocalDateTimeInputValue
// produces) into the date + 12h-clock parts the schedule picker edits.
function parseScheduleValue(value) {
  if (!value) return { date: "", hour12: 12, minute: 0, second: 0, period: "PM" };
  const [datePart, timePart = "00:00:00"] = value.split("T");
  const [hStr, mStr, sStr] = timePart.split(":");
  const hour24 = parseInt(hStr, 10) || 0;
  const minute = parseInt(mStr, 10) || 0;
  const second = parseInt(sStr, 10) || 0;
  const period = hour24 >= 12 ? "PM" : "AM";
  const hour12 = hour24 % 12 === 0 ? 12 : hour24 % 12;
  return { date: datePart, hour12, minute, second, period };
}

function buildScheduleValue(date, hour12, minute, second, period) {
  if (!date) return "";
  let hour24 = hour12 % 12;
  if (period === "PM") hour24 += 12;
  return `${date}T${String(hour24).padStart(2, "0")}:${String(minute).padStart(2, "0")}:${String(second).padStart(2, "0")}`;
}

const SCHEDULE_HOUR_OPTIONS = Array.from({ length: 12 }, (_, i) => i + 1);
const SCHEDULE_MINUTE_OPTIONS = Array.from({ length: 60 }, (_, i) => i);
const SCHEDULE_SECOND_OPTIONS = SCHEDULE_MINUTE_OPTIONS;

// Native `datetime-local` renders as a 24h clock on most locales/OSes (see
// the Publish modal screenshot this replaced) which admins misread -- this
// splits it into a plain date field plus an explicit hour/minute/second/AM-PM
// picker so the scheduled time reads unambiguously. Still stores/emits the
// same "YYYY-MM-DDTHH:mm:ss" string the rest of the publish flow expects.
function ScheduleDateTimeInput({ value, onChange, minValue }) {
  const parts = parseScheduleValue(value);
  const minDateOnly = minValue ? minValue.split("T")[0] : undefined;

  const emit = (next) => {
    onChange(buildScheduleValue(next.date, next.hour12, next.minute, next.second, next.period));
  };

  return (
    <div className={styles.scheduleDateTimeRow}>
      <div className={styles.scheduleField}>
        <label className={styles.label}>Date</label>
        <input
          type="date"
          className={`${styles.input} ${styles.scheduleDateInput}`}
          value={parts.date}
          min={minDateOnly}
          onChange={(e) => emit({ ...parts, date: e.target.value })}
        />
      </div>
      <div className={styles.scheduleField}>
        <label className={styles.label}>Time</label>
        <div className={styles.scheduleTimeRow}>
          <NumberDropdown
            value={parts.hour12}
            options={SCHEDULE_HOUR_OPTIONS}
            disabled={!parts.date}
            ariaLabel="Hour"
            onChange={(h) => emit({ ...parts, hour12: h })}
          />
          <span className={styles.scheduleTimeColon}>:</span>
          <NumberDropdown
            value={parts.minute}
            options={SCHEDULE_MINUTE_OPTIONS}
            disabled={!parts.date}
            ariaLabel="Minute"
            onChange={(m) => emit({ ...parts, minute: m })}
          />
          <span className={styles.scheduleTimeColon}>:</span>
          <NumberDropdown
            value={parts.second}
            options={SCHEDULE_SECOND_OPTIONS}
            disabled={!parts.date}
            ariaLabel="Second"
            onChange={(s) => emit({ ...parts, second: s })}
          />
          <div className={styles.schedulePeriodToggle}>
            {["AM", "PM"].map((p) => (
              <button
                key={p}
                type="button"
                disabled={!parts.date}
                className={`${styles.schedulePeriodBtn} ${parts.period === p ? styles.schedulePeriodBtnActive : ""}`}
                onClick={() => emit({ ...parts, period: p })}
              >
                {p}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// Deterministic per-category "shuffle" -- seeded on the category id so the
// order stays stable across re-renders instead of re-shuffling on every
// keystroke elsewhere in the editor (a real Math.random() shuffle would).
function seededShuffle(items, seed) {
  const arr = [...items];
  // Hash non-numeric ids (e.g. UUIDs) to a number -- a string seed would make
  // every rand() NaN and the swaps below would fill the list with undefined.
  let s = Number(seed);
  if (!Number.isFinite(s) || s <= 0) {
    s = String(seed ?? "").split("").reduce((h, ch) => (h * 31 + ch.charCodeAt(0)) % 233280, 0);
  }
  s = s || 1;
  const rand = () => {
    s = (s * 9301 + 49297) % 233280;
    return s / 233280;
  };
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(rand() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

function withConfigDefaults(config, layoutType) {
  const mergedCardsVisible = { ...DEFAULT_CATEGORY_CONFIG.cardsVisible, ...(config?.cardsVisible || {}) };
  // A category that never had its "Image ratio" control touched has no
  // cardDesign.imageRatio saved at all -- defaulting it straight to "2:3"
  // here (instead of picking it up from LAYOUT_ASPECT_RATIO first) would
  // make every circle/square/16:9 layout permanently indistinguishable from
  // a 2:3 one, since an explicit value and this default look identical once
  // merged in.
  const defaultImageRatio = LAYOUT_ASPECT_RATIO[layoutType] || DEFAULT_CARD_DESIGN.imageRatio;
  return {
    ...config,
    dataSource: config?.dataSource || DEFAULT_CATEGORY_CONFIG.dataSource,
    cardDesign: {
      ...DEFAULT_CARD_DESIGN,
      imageRatio: defaultImageRatio,
      ...(config?.cardDesign || {}),
    },
    // Clamps values saved before the 1-50 range was enforced, so an old
    // section doesn't resurrect an out-of-range count just by being reopened.
    cardsVisible: Object.fromEntries(
      Object.entries(mergedCardsVisible).map(([device, count]) => [
        device,
        Math.min(CARDS_VISIBLE_MAX, Math.max(CARDS_VISIBLE_MIN, count)),
      ])
    ),
    behaviour: { ...DEFAULT_BEHAVIOUR, ...(config?.behaviour || {}) },
  };
}

const PAGE_TEMPLATES = [
  {
    key: "movie-landing",
    label: "Movie Landing",
    description: "Hero + new releases, Top 10, genres",
    icon: <IoGridOutline />,
    starterCategories: [
      { name: "Hero", layout_type: "hero-banner" },
      { name: "New Releases", layout_type: "row-2x3" },
      { name: "Top 10", layout_type: "top10" },
      { name: "Genres", layout_type: "circle" },
      { name: "Editorial Picks", layout_type: "editorial-collection" },
    ],
  },
  {
    key: "live-sports",
    label: "Live Sports",
    description: "Live now, upcoming events, Top 10",
    icon: <IoRadioButtonOnOutline />,
    starterCategories: [
      { name: "Live Now", layout_type: "hero-banner" },
      { name: "Upcoming Events", layout_type: "row-16x9" },
      { name: "Top 10", layout_type: "top10" },
      { name: "Highlights", layout_type: "row-16x9" },
    ],
  },
  {
    key: "kids-home",
    label: "Kids Home",
    description: "Playful hero, characters, learn & play",
    icon: <IoPieChartOutline />,
    starterCategories: [
      { name: "Featured", layout_type: "hero-banner" },
      { name: "Characters", layout_type: "circle" },
      { name: "Learn & Play", layout_type: "square" },
      { name: "Popular with Kids", layout_type: "row-2x3" },
    ],
  },
  {
    key: "series-hub",
    label: "Series Hub",
    description: "Featured carousel + bingeable rails",
    icon: <IoDiamondOutline />,
    starterCategories: [
      { name: "Featured", layout_type: "carousel" },
      { name: "Bingeable Series", layout_type: "row-2x3" },
      { name: "New Episodes", layout_type: "row-16x9" },
      { name: "Genres", layout_type: "circle" },
    ],
  },
  {
    key: "streaming-home",
    label: "Streaming Home",
    description: "Everything: hero, continue, trending",
    icon: <IoHomeOutline />,
    starterCategories: [
      { name: "Hero Banner", layout_type: "hero-banner" },
      {
        name: "Continue Watching",
        layout_type: "row-2x3",
        config: { ...DEFAULT_CATEGORY_CONFIG, dataSource: "continue-watching" },
      },
      {
        name: "Trending Now",
        layout_type: "row-2x3",
        config: { ...DEFAULT_CATEGORY_CONFIG, dataSource: "trending" },
      },
      { name: "Top 10", layout_type: "top10" },
      { name: "New Releases", layout_type: "row-16x9" },
      { name: "Genres", layout_type: "circle" },
    ],
  },
  {
    key: "blank",
    label: "Blank Page",
    description: "Start from scratch",
    icon: <IoAddOutline />,
    starterCategories: [],
  },
];

// Renders into document.body and pins itself to `anchorRef` with fixed
// coordinates so it can escape an ancestor's `overflow-y: auto` (the sidebar
// clips absolutely-positioned children instead of letting them float over it).
function AnchoredPopover({ anchorRef, popoverRef, open, className, children }) {
  const elRef = useRef(null);
  const [coords, setCoords] = useState(null);

  useLayoutEffect(() => {
    if (!open || !anchorRef.current) {
      setCoords(null);
      return undefined;
    }
    const reposition = () => {
      const anchor = anchorRef.current;
      if (!anchor) return;
      const anchorRect = anchor.getBoundingClientRect();
      const popoverRect = elRef.current ? elRef.current.getBoundingClientRect() : null;
      const width = popoverRect ? popoverRect.width : 240;
      const height = popoverRect ? popoverRect.height : 0;

      let left = anchorRect.left;
      if (left + width > window.innerWidth - 12) {
        left = Math.max(12, window.innerWidth - width - 12);
      }

      let top = anchorRect.bottom + 10;
      if (height && top + height > window.innerHeight - 12) {
        top = Math.max(12, anchorRect.top - 10 - height);
      }

      setCoords({ top, left });
    };
    reposition();
    window.addEventListener("scroll", reposition, true);
    window.addEventListener("resize", reposition);
    return () => {
      window.removeEventListener("scroll", reposition, true);
      window.removeEventListener("resize", reposition);
    };
  }, [open, anchorRef]);

  if (!open) return null;

  return createPortal(
    <div
      ref={(node) => {
        elRef.current = node;
        if (popoverRef) popoverRef.current = node;
      }}
      className={className}
      style={{
        position: "fixed",
        top: coords ? coords.top : -9999,
        left: coords ? coords.left : -9999,
        visibility: coords ? "visible" : "hidden",
        margin: 0,
      }}
    >
      {children}
    </div>,
    document.body
  );
}

// A custom hour/minute/second picker for ScheduleDateTimeInput -- a native
// `<select>` with 60 options pops open as a raw, unstyled OS list with no
// height cap (see the screenshot this replaced, which spilled most of the
// way down the page). Built on AnchoredPopover so the options list is a
// normal styled div we can cap with max-height + scroll, portaled past the
// publish modal's own `overflow-y: auto` instead of being clipped by it.
function NumberDropdown({ value, options, disabled, onChange, ariaLabel }) {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef(null);
  const popoverRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onClickOutside = (e) => {
      if (
        anchorRef.current &&
        !anchorRef.current.contains(e.target) &&
        !(popoverRef.current && popoverRef.current.contains(e.target))
      ) {
        setOpen(false);
      }
    };
    const onKeyDown = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onClickOutside);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onClickOutside);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    popoverRef.current
      ?.querySelector('[data-active="true"]')
      ?.scrollIntoView({ block: "nearest" });
  }, [open]);

  return (
    <>
      <button
        type="button"
        ref={anchorRef}
        className={`${styles.input} ${styles.scheduleTimeSelect} ${styles.numberDropdownTrigger}`}
        disabled={disabled}
        aria-label={ariaLabel}
        onClick={() => setOpen((o) => !o)}
      >
        {String(value).padStart(2, "0")}
      </button>
      <AnchoredPopover
        anchorRef={anchorRef}
        popoverRef={popoverRef}
        open={open}
        className={styles.numberDropdownList}
      >
        {options.map((opt) => (
          <div
            key={opt}
            data-active={opt === value}
            className={`${styles.numberDropdownOption} ${opt === value ? styles.numberDropdownOptionActive : ""}`}
            onClick={() => {
              onChange(opt);
              setOpen(false);
            }}
          >
            {String(opt).padStart(2, "0")}
          </div>
        ))}
      </AnchoredPopover>
    </>
  );
}

// Same portal-escape trick as AnchoredPopover, but anchored to an explicit
// {x, y} viewport point (the right-click location) instead of an element --
// used for the layer row context menu, which has no anchor element.
function ContextMenu({ x, y, popoverRef, onClose, children }) {
  const elRef = useRef(null);
  const [coords, setCoords] = useState({ top: y, left: x, visibility: "hidden" });

  useLayoutEffect(() => {
    const el = elRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    let left = x;
    if (left + rect.width > window.innerWidth - 8) {
      left = Math.max(8, window.innerWidth - rect.width - 8);
    }
    let top = y;
    if (top + rect.height > window.innerHeight - 8) {
      top = Math.max(8, window.innerHeight - rect.height - 8);
    }
    setCoords({ top, left, visibility: "visible" });
  }, [x, y]);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  return createPortal(
    <div
      ref={(node) => {
        elRef.current = node;
        if (popoverRef) popoverRef.current = node;
      }}
      className={styles.layerContextMenu}
      style={{
        position: "fixed",
        top: coords.top,
        left: coords.left,
        visibility: coords.visibility,
        margin: 0,
      }}
    >
      {children}
    </div>,
    document.body
  );
}

// Banner preview with more than one selected item becomes a slider: dot
// indicators + prev/next arrows, and (when the section's "Auto scroll"
// behaviour is on) a timer that advances it on its own -- same idea as the
// rail's auto-scroll marquee, just paged instead of continuous since a
// banner shows one full-bleed item at a time rather than a scrolling strip.
function BannerCarousel({
  items,
  name,
  cardDesign,
  autoScroll,
  infiniteScroll,
  landscapeOverrideItems,
  setLandscapeOverrideItems,
}) {
  const [index, setIndex] = useState(0);

  // Selecting/removing content can shrink the list out from under the
  // current index -- clamp back onto a real item instead of rendering blank.
  useEffect(() => {
    if (index >= items.length) setIndex(0);
  }, [items.length, index]);

  useEffect(() => {
    if (!autoScroll || items.length < 2) return undefined;
    // Without "Infinite scroll" the slider plays through once and holds on
    // the last slide, same as the rail marquee's one-pass mode.
    if (!infiniteScroll && index >= items.length - 1) return undefined;
    const timer = setInterval(() => {
      setIndex((i) => (i + 1) % items.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [autoScroll, infiniteScroll, items.length, index]);

  const item = items[index] || items[0];
  const isBannerLandscape = landscapeOverrideItems.has(item.id);
  const showNav = items.length > 1;

  return (
    <div className={styles.bannerPreview} style={{ aspectRatio: ratioToCss(cardDesign.imageRatio) }}>
      <img
        src={item.landscape || item.thumbnail || item.portrait}
        className={styles.bannerImage}
        style={
          isBannerLandscape
            ? { objectFit: "contain", backgroundColor: "#000" }
            : undefined
        }
        alt=""
        onLoad={(e) => {
          const img = e.currentTarget;
          const isActuallyLandscape = img.naturalWidth > img.naturalHeight;
          setLandscapeOverrideItems((prev) => {
            const already = prev.has(item.id);
            if (isActuallyLandscape === already) return prev;
            const next = new Set(prev);
            if (isActuallyLandscape) next.add(item.id);
            else next.delete(item.id);
            return next;
          });
        }}
      />
      <div className={styles.bannerOverlay}>
        <span className={styles.bannerEyebrow}>{name}</span>
        <h3 className={styles.bannerTitle}>{item.title || item.name}</h3>
        {item.description && (
          <p className={styles.bannerDescription}>{item.description}</p>
        )}
      </div>
      {showNav && (
        <>
          <button
            type="button"
            className={`${styles.bannerNavArrow} ${styles.bannerNavArrowPrev}`}
            onClick={(e) => {
              e.stopPropagation();
              setIndex((i) => (i - 1 + items.length) % items.length);
            }}
            title="Previous"
          >
            <IoArrowBackOutline />
          </button>
          <button
            type="button"
            className={`${styles.bannerNavArrow} ${styles.bannerNavArrowNext}`}
            onClick={(e) => {
              e.stopPropagation();
              setIndex((i) => (i + 1) % items.length);
            }}
            title="Next"
          >
            <IoArrowForwardOutline />
          </button>
          <div className={styles.bannerDots}>
            {items.map((_, i) => (
              <span
                key={i}
                className={`${styles.bannerDot} ${i === index ? styles.bannerDotActive : ""}`}
                onClick={(e) => {
                  e.stopPropagation();
                  setIndex(i);
                }}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// A card rail that only auto-scrolls when its cards actually overflow it.
// Whether they do can't be known from the item count -- it depends on the
// card shape (circles are narrow, 16:9 cards wide), the device preset, and
// whether the side panels are open -- so it's measured, and re-measured
// whenever the rail or any card resizes. `onOverflowChange` lets the
// Section Settings panel enable/disable the Auto scroll toggle to match.
function RailViewport({
  className,
  itemCount,
  autoScroll,
  infiniteScroll,
  onOverflowChange,
  renderCards,
}) {
  const railRef = useRef(null);
  const [overflows, setOverflows] = useState(false);
  const active = autoScroll && overflows;

  useLayoutEffect(() => {
    const rail = railRef.current;
    if (!rail) return undefined;
    // While animating, the cards live inside the doubled marquee track; only
    // the first copy counts. offsetLeft ignores the marquee's transform.
    const container = rail.querySelector(`.${styles.railTrackAutoScroll}`) || rail;
    const measure = () => {
      const cards = container.children;
      const count = Math.min(itemCount, cards.length);
      if (!count) {
        setOverflows(false);
        return;
      }
      const first = cards[0];
      const last = cards[count - 1];
      const contentWidth = last.offsetLeft + last.offsetWidth - first.offsetLeft;
      const cs = window.getComputedStyle(rail);
      const viewport =
        rail.clientWidth - parseFloat(cs.paddingLeft || 0) - parseFloat(cs.paddingRight || 0);
      setOverflows(contentWidth > viewport + 1);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(rail);
    Array.from(container.children).forEach((card) => ro.observe(card));
    return () => ro.disconnect();
  });

  useEffect(() => {
    onOverflowChange?.(overflows);
  }, [overflows, onOverflowChange]);
  // Unmounting (category switched, layout changed to a banner...) must not
  // leave a stale "overflows" behind for the next selection.
  useEffect(() => () => onOverflowChange?.(false), [onOverflowChange]);

  return (
    <div
      ref={railRef}
      className={className}
      style={active ? { overflowX: "hidden" } : undefined}
    >
      {active ? (
        <div
          className={`${styles.railTrackAutoScroll} ${infiniteScroll ? styles.railTrackAutoScrollLoop : styles.railTrackAutoScrollOnce}`}
          style={{ animationDuration: `${itemCount * 3}s` }}
        >
          {renderCards(true)}
        </div>
      ) : (
        renderCards(false)
      )}
    </div>
  );
}

export default function HomePageBuilder() {
  const navigate = useNavigate();
  const { state } = useLocation();
  const { can } = useRbac();
  const appConfig = useAppConfig();
  // The tenant's website (its primary domain from Domain details), which the
  // rail "View All" button opens. The domain is stored as a bare host.
  const { tenant } = useTenant() || {};
  const websiteUrl = tenant?.primaryDomain
    ? /^https?:\/\//i.test(tenant.primaryDomain)
      ? tenant.primaryDomain
      : `${window.location.protocol}//${tenant.primaryDomain}`
    : null;
  const canCreate = can("categories", "create");
  const canEdit = can("categories", "edit");
  // Whether the selected section's rail overflows the canvas row -- reported
  // by its RailViewport; gates the Auto scroll toggle.
  const [selectedRailOverflows, setSelectedRailOverflows] = useState(false);
  const canDelete = can("categories", "delete");
  const canEditTheme = can("settings", "edit");

  const [isNew, setIsNew] = useState(true);
  const [categories, setCategories] = useState([]);
  const [selected, setSelected] = useState(null);
  // Mirrors `selected?.id` for code that's already mid-async (e.g. a save's
  // in-flight request) and needs to know, once it resolves, whether the
  // admin has since switched to a different category -- a plain closure
  // over `selected` would only ever see the value from when it started.
  const selectedIdRef = useRef(null);
  // Same, but also tells unsaved drafts apart (their id is always "") -- used
  // by addContentForGenres to drop results for a category that's no longer open.
  const selectedKeyRef = useRef(null);
  useEffect(() => {
    selectedIdRef.current = selected?.id ?? null;
    selectedKeyRef.current = selected?.id || selected?.draftKey || null;
  }, [selected]);
  // draftKey -> id once a draft is first saved, so an in-flight genre fetch
  // started on the draft still applies to the same (now saved) category.
  const savedDraftIdsRef = useRef({});
  // Per category key: { matches: {genreId: Set<contentId>}, added: Set<contentId> }
  // -- which titles each genre pulled in, and which of those weren't already
  // in Selected Content, so removing a genre chip can take back only its own.
  const genreAddedRef = useRef({});
  const [tagInput, setTagInput] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [genres, setGenres] = useState([]);
  const [genreContentLoading, setGenreContentLoading] = useState(false);
  const [dragCat, setDragCat] = useState(null);
  const [dragOverCat, setDragOverCat] = useState(null);
  // Draft page order (section ids) from drag-and-drop in the Pages panel.
  // Stays local until the Publish modal is confirmed -- see persistPageOrder.
  const [pageOrder, setPageOrder] = useState(null);
  const dragPageIdRef = useRef(null);
  const sidebarRef = useRef(null);
  const dragScrollY = useRef(null);
  const dragScrollFrame = useRef(null);
  useEffect(() => {
    return () => {
      if (dragScrollFrame.current) cancelAnimationFrame(dragScrollFrame.current);
    };
  }, []);
  const [draggedItem, setDraggedItem] = useState(null);
  const [videoSearch, setVideoSearch] = useState("");
  const [sections, setSections] = useState([]);
  const [activeSectionId, setActiveSectionId] = useState(null);
  const [sectionModalOpen, setSectionModalOpen] = useState(false);
  const [publishModalOpen, setPublishModalOpen] = useState(false);
  const [publishChanges, setPublishChanges] = useState([]);
  const [publishChangesLoading, setPublishChangesLoading] = useState(false);
  const [publishTargets, setPublishTargets] = useState(["web", "tv", "mobile"]);
  const [scheduleEnabled, setScheduleEnabled] = useState(false);
  const [scheduleAt, setScheduleAt] = useState("");
  const [editingSection, setEditingSection] = useState(null);
  const [leftTab, setLeftTab] = useState("widgets");
  const [leftCollapsed, setLeftCollapsed] = useState(true);
  const [rightCollapsed, setRightCollapsed] = useState(true);
  const [dirty, setDirty] = useState(false);
  // Holds the key(s) (see `savingKey` in persistCategory) of whichever
  // category/categories currently have a save in flight. Scoped per-category
  // on purpose: while category A's save is in flight, switching to and
  // autosaving category B must NOT be blocked by A's save still being in
  // progress -- only a second concurrent save of that *same* open category
  // (the actual duplicate-POST risk for a new category) should be.
  const savingRef = useRef(new Set());
  // A never-persisted draft has no real id yet (createCategory sets id: ""
  // for all of them), so persistCategory can't key its save-in-flight guard
  // on id alone without every draft colliding. Each draft gets one of these,
  // assigned once at creation and carried through every `{ ...selected, ... }`
  // update afterwards (editing tags, etc. never replaces it) -- unlike the
  // `selected` object reference itself, which those same updates DO replace
  // on every edit, so identity alone can't be used as a stable key either.
  const draftKeyRef = useRef(0);
  const [device, setDevice] = useState("web");
  const [isDragOverCanvas, setIsDragOverCanvas] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [templatesOpen, setTemplatesOpen] = useState(false);
  const [versionHistoryOpen, setVersionHistoryOpen] = useState(false);
  const [versionHistoryLoading, setVersionHistoryLoading] = useState(false);
  const [versionHistoryError, setVersionHistoryError] = useState(null);
  const [versions, setVersions] = useState([]);
  const [restoringVersionId, setRestoringVersionId] = useState(null);
  const [pendingTemplate, setPendingTemplate] = useState(null);
  const [pendingDeletePage, setPendingDeletePage] = useState(null);
  const [pendingDeleteCategory, setPendingDeleteCategory] = useState(null);
  // Undo/Redo history: page-add and category add/delete/reorder actions
  // push a command here. Add/delete commands re-create/re-delete through the
  // same backend calls the original action used (there's no server-side undo
  // API) rather than mutating local state, since every mutation here is
  // server-authoritative and gets refetched anyway. A re-created row gets a
  // new id, so `runUndo`/`runRedo` rewrite the command's id in place before
  // moving it to the other stack -- otherwise the next undo of it would
  // target the row that no longer exists.
  const [undoStack, setUndoStack] = useState([]);
  const [redoStack, setRedoStack] = useState([]);
  const historyBusyRef = useRef(false);
  const [theme, setTheme] = useState(null);
  const [accentPickerOpen, setAccentPickerOpen] = useState(false);
  const [customColorInput, setCustomColorInput] = useState("#3E7BFA");
  const [customColorError, setCustomColorError] = useState("");
  const accentPickerRef = useRef(null);
  const accentPopoverRef = useRef(null);

  const [bgPickerOpen, setBgPickerOpen] = useState(false);
  const [customBgInput, setCustomBgInput] = useState("#1A1A1A");
  const [customBgError, setCustomBgError] = useState("");
  const bgPickerRef = useRef(null);
  const bgPopoverRef = useRef(null);

  // Font color picked in the Theme panel but not yet live -- previewed
  // locally and only saved (so the website picks it up) on Publish.
  const [draftFontColor, setDraftFontColor] = useState(null);
  const [fontPickerOpen, setFontPickerOpen] = useState(false);
  const [customFontInput, setCustomFontInput] = useState("#FFFFFF");
  const [customFontError, setCustomFontError] = useState("");
  const fontPickerRef = useRef(null);
  const fontPopoverRef = useRef(null);

  useEffect(() => {
    if (!accentPickerOpen && !bgPickerOpen && !fontPickerOpen) return;
    const onClickOutside = (e) => {
      if (
        accentPickerOpen &&
        accentPickerRef.current &&
        !accentPickerRef.current.contains(e.target) &&
        !(accentPopoverRef.current && accentPopoverRef.current.contains(e.target))
      ) {
        setAccentPickerOpen(false);
      }
      if (
        bgPickerOpen &&
        bgPickerRef.current &&
        !bgPickerRef.current.contains(e.target) &&
        !(bgPopoverRef.current && bgPopoverRef.current.contains(e.target))
      ) {
        setBgPickerOpen(false);
      }
      if (
        fontPickerOpen &&
        fontPickerRef.current &&
        !fontPickerRef.current.contains(e.target) &&
        !(fontPopoverRef.current && fontPopoverRef.current.contains(e.target))
      ) {
        setFontPickerOpen(false);
      }
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, [accentPickerOpen, bgPickerOpen, fontPickerOpen]);

  // Right-click menu (Duplicate / Move to top / Delete) for a Layers panel
  // row -- { cat, x, y } where x/y is the click point the menu anchors to.
  const [layerMenu, setLayerMenu] = useState(null);
  const layerMenuRef = useRef(null);

  useEffect(() => {
    if (!layerMenu) return;
    const onClickOutside = (e) => {
      if (layerMenuRef.current && !layerMenuRef.current.contains(e.target)) {
        setLayerMenu(null);
      }
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, [layerMenu]);

  // Right-click menu (Rename / Duplicate / Set as home / Show-Hide / Delete)
  // for a Pages panel row -- { sec, x, y } where x/y is the click point.
  const [pageMenu, setPageMenu] = useState(null);
  const pageMenuRef = useRef(null);

  useEffect(() => {
    if (!pageMenu) return;
    const onClickOutside = (e) => {
      if (pageMenuRef.current && !pageMenuRef.current.contains(e.target)) {
        setPageMenu(null);
      }
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, [pageMenu]);

  // Inline rename of a page's name in the Pages panel -- { id, value }.
  // Started by double-clicking the name or the context menu's "Rename".
  const [editingPageName, setEditingPageName] = useState(null);
  const editingPageNameRef = useRef(null);

  useEffect(() => {
    if (editingPageName && editingPageNameRef.current) {
      editingPageNameRef.current.focus();
      editingPageNameRef.current.select();
    }
  }, [editingPageName?.id]);

  const startRenamePage = (sec) => {
    if (!canEdit) {
      showToast("You don't have permission to edit pages", "error");
      return;
    }
    setEditingPageName({ id: sec.id, value: sec.name });
  };

  const commitRenamePage = async () => {
    if (!editingPageName) return;
    const { id, value } = editingPageName;
    const trimmed = value.trim();
    const original = sections.find((s) => s.id === id);
    setEditingPageName(null);
    if (!trimmed || !original || trimmed === original.name) return;
    try {
      await updateSections(id, { name: trimmed });
      setSections((prev) => prev.map((s) => (s.id === id ? { ...s, name: trimmed } : s)));
      showToast("Page renamed — publish to update the site", "success");
    } catch (err) {
      showToast(err?.response?.data?.message || "Error renaming page", "error");
    }
  };

  const [widgetGroups, setWidgetGroups] = useState([]);
  const [landscapeOverrideItems, setLandscapeOverrideItems] = useState(() => new Set());
  const scrollRef = useRef(null);
  const sectionsRef = useRef([]);
  const fetchDataRequestRef = useRef(0);
  const SCROLL_SPEED = 12;
  const EDGE_OFFSET = 60;

  const categoryBlockRefs = useRef({});

  const [postFormData, setPostFormData] = useState({
    name: "",
    layout_type: "",
    genres: [],
    contents: [],
    videos: [],
    contents_payload: [],
    config: DEFAULT_CATEGORY_CONFIG,
  });

  const fetchSectionsList = async ({ preferredId, selectNewest } = {}) => {
    try {
      const response = await getSections(1, 500);
      const rows = response.data?.data || [];
      const prevIds = new Set(sectionsRef.current.map((s) => s.id));
      sectionsRef.current = rows;
      setSections(rows);

      setActiveSectionId((prevActive) => {
        if (selectNewest) {
          const newest = rows.find((s) => !prevIds.has(s.id));
          if (newest) return newest.id;
        }
        const target = preferredId ?? prevActive;
        if (target && rows.some((s) => s.id === target)) return target;
        return rows[0]?.id || null;
      });
    } catch (err) {
      showToast(err?.response?.data?.message || "Error fetching pages", "error");
    }
  };

  // `syncPanel: false` refreshes the categories list (for the Layers panel
  // and canvas) without touching postFormData/selected/dirty -- used when the
  // refresh is a side-effect of reordering/deleting a *different* category
  // than the one currently open for editing, so it doesn't clobber whatever
  // is half-typed in the right-hand panel.
  //
  // `staleIfSelectionChangedFrom` guards a slower kind of staleness: a caller
  // that kicked this off *because* a specific category was selected (e.g. a
  // save just finished for it) needs that check re-run right here, after
  // this function's own `getCategories` await, not before it -- the admin
  // can switch to a different category during that gap too. Pass the id
  // that was selected when the caller started; if the live selection has
  // since moved away from it, this result is stale for the panel and must
  // not clobber it (the list refresh above still applies either way).
  const fetchData = async (
    selectedId = null,
    { syncPanel = true, staleIfSelectionChangedFrom } = {}
  ) => {
    const requestId = ++fetchDataRequestRef.current;
    const sectionId = activeSectionId;
    try {
      const response = await getCategories(sectionId);
      // A faster, more recent call to fetchData (e.g. from switching pages
      // again before this one resolved) already applied its own result --
      // applying this stale one now would overwrite it.
      if (requestId !== fetchDataRequestRef.current) return;
      const sectionData = response.data?.data || {};
      // Cards are keyed by content ref (series id negated) so a movie and a
      // series that share an id are never treated as the same card.
      const data = (sectionData.categories || []).map((cat) => ({
        ...cat,
        contents_data: (cat.contents_data || []).map(withContentRef),
      }));
      setCategories(data);
      // Category-level mutations (add/edit/delete/reorder a card) only draft
      // the individual SectionCategory now -- they never touch Section.is_active
      // (see SectionController: storeCategory/updateCategory/deleteContent/
      // deleteGenre draft just the category they touch). The Publish toggle and
      // the Publish modal's confirmPublish already call fetchSectionsList() to
      // refresh this themselves after they change it. This sync is still worth
      // doing here, for free, off data this call already fetched: it catches
      // the section's is_active/last_published_at/scheduled_publish_at drifting
      // for reasons outside this session -- ScheduledPublishJob flipping a
      // scheduled page live in the background, or another admin publishing the
      // same page concurrently -- so the Live/Draft pill and "what changed"
      // diff (driven by `currentSection.is_active`/`last_published_at`) don't
      // keep showing stale state until the next full fetchSectionsList().
      setSections((prev) =>
        prev.map((s) =>
          s.id === sectionId
            ? {
                ...s,
                is_active: sectionData.is_active,
                last_published_at: sectionData.last_published_at,
                scheduled_publish_at: sectionData.scheduled_publish_at,
              }
            : s
        )
      );
      if (!syncPanel) return;
      if (
        staleIfSelectionChangedFrom !== undefined &&
        selectedIdRef.current !== staleIfSelectionChangedFrom
      ) {
        return;
      }
      if (data.length) {
        const currentId = selectedId || selected?.id;
        const selectedItem =
          data?.find((cat) => cat?.id === currentId) || data[0];
        const videosData = [...(selectedItem?.contents_data || [])];
        setPostFormData({
          name: selectedItem.name,
          layout_type: selectedItem.layout_type,
          genres: selectedItem.genres || [],
          contents: selectedItem.contents_data || [],
          videos: videosData,
          contents_payload: selectedItem.contents || [],
          config: withConfigDefaults(selectedItem.config, selectedItem.layout_type),
        });
        setIsNew(false);
        setSelected(selectedItem);
        setDirty(false);
      } else {
        setSelected(null);
      }
    } catch (err) {
      if (requestId !== fetchDataRequestRef.current) return;
      showToast(err?.response?.data?.message, "error");
    }
  };

  const pushHistory = (command) => {
    setUndoStack((prev) => [...prev, command]);
    setRedoStack([]);
  };

  // Reverses (direction: "undo") or re-applies (direction: "redo") one
  // history command. Add/delete are inverses of each other; reorder is its
  // own inverse (swapping the same pair twice restores the original order).
  // Returns the command to file back onto the *other* stack -- for add and
  // delete this is a new object carrying whatever id the backend just
  // assigned, since the original id is gone after a delete/re-create.
  const applyHistoryCommand = async (command, direction) => {
    // Undo/redo replays the same create/delete/patch calls their direct
    // actions use, so it must be gated by the same permission each of those
    // actions checks -- otherwise an edit-only user could delete/recreate
    // pages or categories, or edit the theme, just by pressing Undo/Redo.
    const requirePermission = (allowed, message) => {
      if (allowed) return;
      throw new Error(message);
    };
    switch (command.type) {
      case "page-add": {
        if (direction === "undo") {
          requirePermission(canDelete, "You don't have permission to delete pages");
          await deleteSections(command.pageId);
          await fetchSectionsList();
          return command;
        }
        requirePermission(canCreate, "You don't have permission to create pages");
        const response = await createSections({
          name: command.name,
          is_active: command.is_active,
        });
        await fetchSectionsList({ selectNewest: true });
        return { ...command, pageId: response?.data?.data?.id };
      }
      case "category-add": {
        if (direction === "undo") {
          requirePermission(canDelete, "You don't have permission to delete sections");
          await deleteCategoriesns(command.categoryId);
          // Don't clobber an unsaved edit open in an unrelated category --
          // unless it's *this* category, which is about to stop existing.
          const syncPanel = !dirty || command.categoryId === selected?.id;
          if (activeSectionId === command.sectionId) await fetchData(undefined, { syncPanel });
          return command;
        }
        requirePermission(canCreate, "You don't have permission to create sections");
        const response = await createCategories(command.sectionId, command.snapshot);
        const newId = response?.data?.data?.id;
        // The re-created category has a brand-new id, so it can never be the
        // one already open in the panel -- only select it into view when
        // there's no unrelated unsaved edit to preserve.
        if (activeSectionId === command.sectionId) {
          await fetchData(newId, { syncPanel: !dirty });
        }
        return { ...command, categoryId: newId };
      }
      case "category-delete": {
        if (direction === "undo") {
          requirePermission(canCreate, "You don't have permission to create sections");
          const response = await createCategories(command.sectionId, command.snapshot);
          const newId = response?.data?.data?.id;
          if (activeSectionId === command.sectionId) {
            await fetchData(newId, { syncPanel: !dirty });
          }
          return { ...command, categoryId: newId };
        }
        requirePermission(canDelete, "You don't have permission to delete sections");
        await deleteCategoriesns(command.categoryId);
        const syncPanel = !dirty || command.categoryId === selected?.id;
        if (activeSectionId === command.sectionId) await fetchData(undefined, { syncPanel });
        return command;
      }
      case "category-reorder": {
        requirePermission(canEdit, "You don't have permission to reorder sections");
        await postSwapOrderCategoyiesList({
          first_id: command.firstId,
          second_id: command.secondId,
        });
        const touchesSelected =
          selected && (command.firstId === selected.id || command.secondId === selected.id);
        const syncPanel = !dirty || touchesSelected;
        if (activeSectionId === command.sectionId) await fetchData(selected?.id, { syncPanel });
        return command;
      }
      case "category-move": {
        requirePermission(canEdit, "You don't have permission to reorder sections");
        // Each swap is its own inverse, so replaying the same pairs in
        // reverse order undoes the whole batch in one shot; forward order
        // re-applies it identically on redo.
        const swaps = direction === "undo" ? [...command.swaps].reverse() : command.swaps;
        for (const { firstId, secondId } of swaps) {
          await postSwapOrderCategoyiesList({ first_id: firstId, second_id: secondId });
        }
        const touchesSelected =
          selected && command.swaps.some((s) => s.firstId === selected.id || s.secondId === selected.id);
        const syncPanel = !dirty || touchesSelected;
        if (activeSectionId === command.sectionId) await fetchData(selected?.id, { syncPanel });
        return command;
      }
      case "theme-patch": {
        requirePermission(canEditTheme, "You don't have permission to edit theme settings");
        applyThemePatch(direction === "undo" ? command.previous : command.patch);
        return command;
      }
      default:
        return command;
    }
  };

  const runUndo = async () => {
    if (historyBusyRef.current || undoStack.length === 0) return;
    historyBusyRef.current = true;
    const command = undoStack[undoStack.length - 1];
    try {
      const settled = await applyHistoryCommand(command, "undo");
      setUndoStack((prev) => prev.slice(0, -1));
      setRedoStack((prev) => [...prev, settled]);
    } catch (err) {
      showToast(err?.response?.data?.message || err?.message || "Error undoing last action", "error");
    } finally {
      historyBusyRef.current = false;
    }
  };

  const runRedo = async () => {
    if (historyBusyRef.current || redoStack.length === 0) return;
    historyBusyRef.current = true;
    const command = redoStack[redoStack.length - 1];
    try {
      const settled = await applyHistoryCommand(command, "redo");
      setRedoStack((prev) => prev.slice(0, -1));
      setUndoStack((prev) => [...prev, settled]);
    } catch (err) {
      showToast(err?.response?.data?.message || err?.message || "Error redoing last action", "error");
    } finally {
      historyBusyRef.current = false;
    }
  };

  const handleVideoSelection = (videos) => {
    const selectedVideos = (Array.isArray(videos)
      ? videos
      : videos
        ? [videos]
        : []).map(withContentRef);

    setPostFormData((prevState) => {
      const prevVideos = Array.isArray(prevState.videos)
        ? prevState.videos
        : [];

      const prevPayload = Array.isArray(prevState.contents_payload)
        ? prevState.contents_payload
        : [];

      const mergedVideos = [...selectedVideos, ...prevVideos];
      const uniqueVideos = Array.from(
        new Map(mergedVideos?.map((v) => [v.id, v])).values(),
      );

      const mergedIds = [...selectedVideos.map((v) => v.id), ...prevPayload];
      const uniqueIds = [...new Set(mergedIds)];

      return {
        ...prevState,
        videos: uniqueVideos,
        contents: uniqueVideos,
        contents_payload: uniqueIds,
      };
    });
    setDirty(true);
  };

  const handleVideoUnCheck = (item) => {
    // A title the admin removes (and maybe re-adds by hand) is no longer genre-added.
    genreAddedRef.current[selected?.id || selected?.draftKey]?.added.delete(item.id);
    setPostFormData((prev) => ({
      ...prev,
      videos: Array.isArray(prev.videos)
        ? prev.videos.filter((v) => v.id !== item.id)
        : [],
      contents: Array.isArray(prev.contents)
        ? prev.contents.filter((v) => v.id !== item.id)
        : [],
      contents_payload: Array.isArray(prev.contents_payload)
        ? prev.contents_payload.filter((id) => id !== item.id)
        : [],
    }));
    setDirty(true);
  };

  // Shared by every place that might navigate away from the currently
  // selected category: with autosave in place there's nothing to prompt for
  // -- just flush any edit that hasn't hit the debounce yet before leaving.
  // Not silent -- unlike the debounce tick, a failure here (empty name, no
  // edit permission) needs to toast its reason, since it also blocks the
  // navigation the admin is actively trying to make.
  // Resolves false only if that save fails -- callers must abort rather than
  // navigate past it.
  const confirmDirtySwitch = async () => {
    if (!dirty || !selected) return true;
    return persistCategory();
  };

  // Adding the same widget twice (or duplicating a section) would otherwise
  // give two sections the identical name -- number the repeats instead
  // ("Featured Carousel", "Featured Carousel 1", "Featured Carousel 2"...) so
  // they're distinguishable in Layers. A trailing number on the source name is
  // treated as an earlier repeat's suffix only when the name without it also
  // exists, so duplicating "X 1" yields "X 2" but "Top 10" stays "Top 10 1".
  const uniqueCategoryName = (name) => {
    if (!name) return name;
    const taken = new Set(categories.map((c) => (c.name || "").trim().toLowerCase()));
    if (!taken.has(name.trim().toLowerCase())) return name;
    const stripped = name.trim().replace(/\s+\d+$/, "");
    const base = taken.has(stripped.toLowerCase()) ? stripped : name.trim();
    let n = 1;
    while (taken.has(`${base} ${n}`.toLowerCase())) n++;
    return `${base} ${n}`;
  };

  const createCategory = async (layoutType = "carousel", label = "") => {
    if (!canCreate) {
      showToast("You don't have permission to add sections", "error");
      return;
    }

    // Algorithmic sources (Trending / Recently Added / Continue Watching)
    // never populate `videos` on the frontend -- their content is computed
    // server-side -- so this check must not apply to them, or the "+" button
    // becomes permanently unusable the moment one is selected. It also only
    // makes sense when a category is actually open for editing -- with
    // nothing selected, postFormData.videos is deliberately empty and there
    // is no "current category" to have missing items.
    if (selected && categories.length > 0 && !isAlgorithmic && !postFormData.videos?.length) {
      showToast("Please add at least 1 item to the current category before creating a new one", "error");
      return;
    }

    if (!(await confirmDirtySwitch())) return;

    label = uniqueCategoryName(label);

    const newCat = {
      id: "",
      name: label,
      items: 0,
      layout_type: layoutType,
      tags: [],
      contents: [],
      // See draftKeyRef's declaration -- stable per-draft identity for
      // persistCategory's save-in-flight guard, since this draft's `id`
      // stays "" (shared by every draft) until it's actually saved.
      draftKey: `draft:${++draftKeyRef.current}`,
      // Carried through to persistCategory's create payload so the backend
      // can insert it at the right sort_order too, instead of always
      // appending -- see insert_after_id handling in storeCategory.
      insertAfterId: selected?.id || null,
    };

    setIsNew(true);
    setPostFormData({
      name: label,
      layout_type: layoutType,
      genres: [],
      contents: [],
      videos: [],
      contents_payload: [],
      // Seeded from the layout's own shape (16:9 row, 1:1 square...) rather
      // than DEFAULT_CATEGORY_CONFIG's blanket "2:3", which would otherwise
      // be saved as an explicit ratio on every new section.
      config: withConfigDefaults({}, layoutType),
    });
    setCategories((prev) => {
      if (prev.length === 0) return [newCat];
      // Insert right after whatever was selected when "+" was clicked, not
      // at the end -- matches drafts (empty id, shared across all of them)
      // by draftKey instead, same distinction persistCategory's savingKey
      // draws. When nothing is selected, `cat.draftKey === selected?.draftKey`
      // would otherwise match the first *persisted* category too (both sides
      // undefined), so guard on `selected` first and append at the end.
      const selectedIndex = selected
        ? prev.findIndex((cat) =>
          selected?.id ? cat.id === selected.id : cat.draftKey === selected?.draftKey,
        )
        : -1;
      if (selectedIndex === -1) return [...prev, newCat];
      return [...prev.slice(0, selectedIndex + 1), newCat, ...prev.slice(selectedIndex + 1)];
    });
    setSelected(newCat);
    // The new block hasn't been rendered/ref'd yet on this tick -- wait for
    // React to commit the categories/selected updates above before scrolling,
    // same as handleLayerClick does for an existing section. Ref is keyed by
    // cat.id, and a never-persisted draft's id is always "" (shared by every
    // draft), which is safe here since only one such draft can exist at once
    // (see the "add at least 1 item" guard above).
    requestAnimationFrame(() => {
      categoryBlockRefs.current[newCat.id]?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  };

  const updateCategory = (field, value) => {
    const updated = { ...selected, [field]: value };
    setSelected(updated);
    setCategories((prev) =>
      prev?.map((cat) => (cat.id === selected.id ? updated : cat)),
    );
  };
  const applySelectCategory = (cat) => {
    // A never-persisted draft (created via createCategory, id: "") stays
    // "new" even after clicking away and back to it -- otherwise the next
    // save would call updateCategories("") instead of createCategories(...).
    setIsNew(!cat?.id);
    setPostFormData({
      name: cat.name,
      layout_type: cat.layout_type,
      genres: cat.genres || [],
      contents: cat.contents_data || [],
      videos: cat?.contents_data || [],
      contents_payload: cat?.contents || [],
      config: withConfigDefaults(cat.config, cat.layout_type),
    });
    setSelected(cat);
    setDirty(false);
  };

  // Guards against silently throwing away unsaved edits: flush a pending
  // autosave for the currently selected category before switching away.
  const handleSelectCategory = async (cat) => {
    if (cat?.id !== selected?.id && !(await confirmDirtySwitch())) return;
    applySelectCategory(cat);
  };
  // Clicking a Layer selects it (same as handleSelectCategory) and also
  // scrolls the stacked canvas to bring that category into view.
  const handleLayerClick = async (cat) => {
    await handleSelectCategory(cat);
    categoryBlockRefs.current[cat.id]?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  // Clicking empty canvas space (not a section block) clears the selection,
  // matching the Figma "no section selected" state -- so the next "+" add
  // appends a new section at the end instead of after whatever was last
  // picked. Mirrors the reset done on page switch (see the activeSectionId
  // effect above).
  const handleCanvasBackgroundClick = async () => {
    if (!selected) return;
    if (!(await confirmDirtySwitch())) return;
    setSelected(null);
    setIsNew(true);
    setPostFormData({
      name: "",
      layout_type: "",
      genres: [],
      contents: [],
      videos: [],
      contents_payload: [],
      config: DEFAULT_CATEGORY_CONFIG,
    });
    setDirty(false);
  };

  // "Genre" data source: pulls every published title tagged with the given
  // genre(s) into Selected Content, paging through /all-content until done.
  const addContentForGenres = async (genreIds) => {
    const ids = (genreIds || []).filter(Boolean);
    if (!ids.length) return;
    // The admin may switch categories while this pages through -- remember
    // which one asked, and drop the results if it's no longer the open one.
    const startKey = selected?.id || selected?.draftKey;
    setGenreContentLoading(true);
    try {
      const limit = 100;
      let page = 1;
      let items = [];
      let total = 0;
      do {
        const res = await getAllContent(page, "all", "", ids.join(","), limit);
        const list = res?.data?.data?.contents || [];
        total = res?.data?.data?.total || 0;
        items = items.concat(list);
        if (!list.length) break;
        page += 1;
      } while (items.length < total);

      const currentKey = selectedKeyRef.current;
      if (currentKey !== startKey && savedDraftIdsRef.current[startKey] !== currentKey) return;

      if (items.length) {
        items = items.map(withContentRef);
        const tracking =
          genreAddedRef.current[currentKey] ||
          (genreAddedRef.current[currentKey] = { matches: {}, added: new Set() });
        const itemIds = items.map((i) => i.id);
        ids.forEach((gid) => {
          tracking.matches[gid] = new Set([...(tracking.matches[gid] || []), ...itemIds]);
        });
        // Only titles not already in Selected Content count as genre-added
        // (idempotent, so safe if React re-runs this updater).
        setPostFormData((prev) => {
          const prevIds = new Set((prev.videos || []).map((v) => v.id));
          itemIds.forEach((id) => {
            if (!prevIds.has(id)) tracking.added.add(id);
          });
          return prev;
        });
        handleVideoSelection(items);
      } else {
        showToast("No published content found for this genre", "info");
      }
    } catch (err) {
      showToast(err?.response?.data?.message || "Error loading genre content", "error");
    } finally {
      setGenreContentLoading(false);
    }
  };

  const handleTagsAdd = (value) => {
    if (!selected || !value) return;
    const tagId = Number(value);
    const updatedTags = Array.from(new Set([...(selected.tags || []), tagId]));
    const selectedGenre = genres?.find((g) => g.id === tagId);
    if (!selectedGenre) return;
    setPostFormData((prev) => {
      const alreadyExists = prev.genres?.some((g) => g.id === selectedGenre.id);
      if (alreadyExists) return prev;
      return {
        ...prev,
        genres: [...(prev.genres || []), selectedGenre],
      };
    });
    updateCategory("tags", updatedTags);
    setTagInput("");
    setDirty(true);
    addContentForGenres([tagId]);
  };

  const handleRemoveTag = async (item) => {
    const removeLocally = () => {
      setPostFormData((prevState) => ({
        ...prevState,
        genres: prevState.genres.filter((e) => e.id !== item.id),
      }));
      setSelected((prev) =>
        prev
          ? {
            ...prev,
            tags: (prev.tags || []).filter((id) => id !== item.id),
          }
          : prev,
      );
    };

    const isPersisted =
      selected?.id &&
      Array.isArray(selected?.genres) &&
      selected.genres.some((g) => g.id === item.id);

    // Titles this genre pulled in during this session (see addContentForGenres)
    // that weren't already selected and aren't matched by another genre still
    // on the section. Untracked (e.g. after a reload) => nothing is removed here.
    const tracking = genreAddedRef.current[selected?.id || selected?.draftKey];
    const matchedByGenre = tracking?.matches[item.id];
    if (tracking) delete tracking.matches[item.id];

    if (!isPersisted) {
      removeLocally();
      if (matchedByGenre) {
        const remaining = (postFormData.genres || []).filter((g) => g.id !== item.id);
        const remainingNames = new Set(remaining.map((g) => g.name));
        const removeIds = new Set(
          (postFormData.videos || [])
            .filter(
              (v) =>
                matchedByGenre.has(v.id) &&
                tracking.added.has(v.id) &&
                !remaining.some((g) => tracking.matches[g.id]?.has(v.id)) &&
                !(Array.isArray(v.genres) && v.genres.some((name) => remainingNames.has(name))),
            )
            .map((v) => v.id),
        );
        if (removeIds.size) {
          removeIds.forEach((id) => tracking.added.delete(id));
          setPostFormData((prev) => ({
            ...prev,
            videos: (prev.videos || []).filter((v) => !removeIds.has(v.id)),
            contents: (prev.contents || []).filter((v) => !removeIds.has(v.id)),
            contents_payload: (prev.contents_payload || []).filter((id) => !removeIds.has(id)),
          }));
        }
      }
      setDirty(true);
      return;
    }

    // Removes the genre and its titles; the backend marks the category as a
    // draft, so the website keeps the old version until the next Publish.
    try {
      const response = await GenresDeletebyId(selected.id, item.id);
      if (response?.data?.status === "success") {
        showToast(response?.data?.message, "success");
        removeLocally();
        await fetchData(selected.id);
      }
    } catch (err) {
      showToast(err?.response?.data?.message, "error");
    }
  };
  const handleBack = async () => {
    if (!(await confirmDirtySwitch())) return;
    navigate(-1);
  };

  const openVersionHistory = async () => {
    if (!currentSection) return;
    setVersionHistoryOpen(true);
    setVersionHistoryLoading(true);
    setVersionHistoryError(null);
    try {
      const response = await getSectionVersions(currentSection.id);
      const list = response?.data?.data ?? response?.data;
      setVersions(Array.isArray(list) ? list : []);
    } catch (err) {
      setVersions([]);
      setVersionHistoryError(
        err?.response?.data?.message || "This page has no saved version history."
      );
    } finally {
      setVersionHistoryLoading(false);
    }
  };

  const handleRestoreVersion = async (version) => {
    if (!currentSection) return;
    setRestoringVersionId(version.id);
    try {
      await restoreSectionVersion(currentSection.id, version.id);
      const rawLabel = version.label || `Version ${version.id}`;
      const label = truncateByLetters(rawLabel, 40);
      showToast(`Restored: ${label}`, "success");
      // A restored version is now folded into the current state, so it no
      // longer represents a distinct point an admin can restore back to.
      setVersions((prev) => prev.filter((v) => v.id !== version.id));
      setVersionHistoryOpen(false);
      await fetchData(currentSection.id);
      fetchSectionsList();
      try {
        const themeResponse = await getTheme();
        setTheme(themeResponse.data?.data || null);
        appConfig.refreshConfig();
      } catch {
        // Theme may not be part of this version's snapshot; ignore.
      }
    } catch (err) {
      showToast(err?.response?.data?.message || "Couldn't restore this version", "error");
    } finally {
      setRestoringVersionId(null);
    }
  };

  const openPublishModal = () => {
    if (!canEdit) {
      showToast("You don't have permission to publish", "error");
      return;
    }
    if (!currentSection) return;
    setPublishTargets(
      currentSection.target_platforms?.length ? currentSection.target_platforms : ["web", "tv", "mobile"]
    );
    setScheduleEnabled(false);
    setScheduleAt("");
    setPublishModalOpen(true);
    loadPublishChanges();
  };

  // "What changed" comes from the server: the builder's working rows (and
  // theme) diffed against the snapshot the public site is serving. Any
  // unsaved edit is flushed first so it shows up in the list.
  const loadPublishChanges = async () => {
    const sectionId = currentSection?.id;
    if (!sectionId) return;
    setPublishChanges([]);
    setPublishChangesLoading(true);
    try {
      if (dirty && selected) await persistCategory({ silent: true });
      const response = await getSectionPendingChanges(sectionId);
      if (response?.data?.status === "success") {
        setPublishChanges(response.data.data?.changes || []);
      }
    } catch (err) {
      showToast(err?.response?.data?.message || "Couldn't load pending changes", "error");
    } finally {
      setPublishChangesLoading(false);
    }
  };

  const togglePublishTarget = (platform) => {
    setPublishTargets((prev) =>
      prev.includes(platform) ? prev.filter((p) => p !== platform) : [...prev, platform]
    );
  };

  const confirmPublish = async () => {
    if (!currentSection) return;

    if (publishTargets.length === 0) {
      showToast("Select at least one platform before publishing", "error");
      return;
    }

    if (dirty && selected) {
      const saved = await persistCategory();
      // persistCategory already toasted the reason -- going live (or
      // scheduling) on top of a save that just failed would report success
      // for a page that doesn't actually reflect the pending edit.
      if (!saved) return;
    }

    if (scheduleEnabled) {
      if (!scheduleAt) {
        showToast("Pick a date and time to schedule for", "error");
        return;
      }
      if (new Date(scheduleAt) <= new Date()) {
        showToast("Scheduled time must be in the future", "error");
        return;
      }
      if (!(await persistPageOrder()) || !(await persistDraftFontColor())) return;
      try {
        const response = await updateSections(currentSection.id, {
          scheduled_publish_at: new Date(scheduleAt).toISOString(),
          target_platforms: publishTargets,
        });
        if (response?.data?.status === "success") {
          showToast("Publish scheduled", "success");
          fetchSectionsList();
        }
      } catch (err) {
        showToast(err?.response?.data?.message, "error");
      }
      setPublishModalOpen(false);
      return;
    }

    if (!(await persistPageOrder()) || !(await persistDraftFontColor())) return;
    try {
      // `publish` is what ships to the site; the backend takes a first-time
      // draft live and otherwise publishes the Pages tab's show/hide as set.
      const response = await updateSections(currentSection.id, {
        publish: true,
        target_platforms: publishTargets,
      });
      if (response?.data?.status === "success") {
        showToast(response?.data?.message || "Published", "success");
        fetchSectionsList();
      }
    } catch (err) {
      showToast(err?.response?.data?.message, "error");
    }
    setPublishModalOpen(false);
  };

  const handleDragStart = (e, item, index) => {
    setDraggedItem({
      id: item.id,
      index,
    });

    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e) => {
    e.preventDefault();

    const container = scrollRef.current;
    if (!container) return;

    const containerRect = container.getBoundingClientRect();
    const mouseY = e.clientY;

    if (mouseY < containerRect.top + EDGE_OFFSET) {
      container.scrollTop -= SCROLL_SPEED;
    }

    if (mouseY > containerRect.bottom - EDGE_OFFSET) {
      container.scrollTop += SCROLL_SPEED;
    }
  };

  const handleDrop = async (e, dropIndex) => {
    e.preventDefault();
    try {
      if (!draggedItem || draggedItem.index === dropIndex) return;

      const draggedId = draggedItem.id;
      const dropPlaceId = postFormData.videos[dropIndex]?.id;

      // A never-persisted draft category (id: "") has nothing on the server
      // to swap sort_order with -- reorder locally only, same guard
      // handleCategoryDragEnd uses for reordering categories themselves.
      // Calling fetchData below for a draft would replace `categories` with
      // the server's list (which can't contain a category that was never
      // saved) and reset the open panel, wiping the section/cards just added.
      if (selected?.id) {
        const response = await postSwapOrderVideoList(selected.id, {
          first_id: draggedId,
          second_id: dropPlaceId,
        });
        showToast(response?.data?.message, "success");
      }

      setPostFormData((prev) => {
        const videos = [...prev.videos];
        const contents = [...prev.contents];

        const [movedVideo] = videos.splice(draggedItem.index, 1);
        videos.splice(dropIndex, 0, movedVideo);

        const [movedContent] = contents.splice(draggedItem.index, 1);
        contents.splice(dropIndex, 0, movedContent);

        return {
          ...prev,
          videos,
          contents,
        };
      });
    } catch (error) {
      showToast(error?.response?.data?.message, "error");
    } finally {
      if (selected?.id) fetchData(selected.id);
    }

    setDraggedItem(null);
  };

  const handleDragEnd = () => {
    setDraggedItem(null);
  };

  const handleDelete = async (videoId) => {
    const confirmDelete = window.confirm(
      "Are you sure you want to delete this media?"
    );
    if (!confirmDelete) return;

    const removeLocally = () => {
      setPostFormData((prevState) => ({
        ...prevState,
        videos: prevState.videos.filter((v) => v.id !== videoId),
        contents: prevState.contents.filter((v) => v.id !== videoId),
        contents_payload: prevState.contents_payload.filter((id) =>
          (typeof id === 'object' ? id.id : id) !== videoId
        ),
      }));
      setDirty(true);
    };

    const isPersisted =
      selected?.id &&
      Array.isArray(selected?.contents_data) &&
      selected.contents_data.some((c) => c.id === videoId);

    if (!isPersisted) {
      removeLocally();
      return;
    }

    try {
      const response = await deleteVideos(selected.id, videoId);
      if (response?.data?.status === "success") {
        showToast(response?.data?.message, "success");
        removeLocally();
      }
    } catch (err) {
      const error = err?.response?.data?.message;
      showToast(error, "error");
    }
  };

  // Persists the currently-selected category's pending edits (name/layout/
  // genres/content/config). Called from the Save button and from every place
  // that needs to save-before-switching (including confirmPublish). Returns
  // whether the save actually succeeded -- callers that gate something on it
  // (publish going live, switching away from the category) must check this
  // rather than assume success, since every failure path here toasts but
  // does not throw.
  // `silent` is set by the autosave effect below so it doesn't spam a
  // success toast on every debounced save -- failures still toast either
  // way, since autosave has no "unsaved" indicator to fall back on.
  const persistCategory = async ({ silent = false } = {}) => {
    if (!selected) return false;
    if (!canEdit && !(isNew && canCreate)) {
      if (!silent) showToast("You don't have permission to save this section", "error");
      return false;
    }
    if (!postFormData?.name?.trim()) {
      if (!silent) showToast("Give this section a name before saving", "error");
      return false;
    }
    // Guards against a second save of this SAME open category landing while
    // one is already in flight -- without it, two overlapping saves of a
    // brand-new category could each POST and create a duplicate. Keyed by
    // id when there is one; a not-yet-saved category has none (id === ""
    // for every draft), so it falls back to its `draftKey` instead -- NOT
    // the `selected` object reference, which editing tags/genres replaces
    // (via `{ ...selected, ... }`) on every change, including mid-save, so
    // it can't be trusted to stay the same key for the same open draft.
    // Scoped per-category so a save of a *different* category is never
    // blocked by this one still being in progress -- e.g. switching away
    // mid-save and autosaving the newly-opened category must proceed.
    // "Already saving this one" isn't a failure (the in-flight save will
    // still land), so it resolves true rather than false -- a caller like
    // confirmDirtySwitch must not read it as "blocked, don't navigate".
    const savingKey = selected?.id || selected?.draftKey;
    if (savingRef.current.has(savingKey)) return true;
    savingRef.current.add(savingKey);

    // The debounced autosave can fire while a "Cards visible" field is
    // mid-edit and briefly empty (see updateCardsVisible) -- sanitize it here
    // so that momentary state never reaches the backend as a raw "".
    const cardsVisible = postFormData?.config?.cardsVisible || {};
    const sanitizedCardsVisible = Object.fromEntries(
      Object.entries(cardsVisible).map(([device, count]) => [
        device,
        Number.isFinite(Number(count)) && count !== "" ? Number(count) : CARDS_VISIBLE_MIN,
      ])
    );

    const payload = {
      name: postFormData?.name,
      layout_type: postFormData?.layout_type,
      genres: postFormData?.genres?.map((item) => item.id),
      contents: (postFormData.videos || []).map((v) => v.id),
      config: { ...postFormData?.config, cardsVisible: sanitizedCardsVisible },
    };

    // Captured before the request goes out -- `selected` itself is a stale
    // closure by the time it resolves, same as any other value here. Passed
    // through to fetchData so *it* can re-check, right before touching the
    // panel, whether the admin has since switched to a different category
    // (fetchData has its own async gap after this point too).
    const savedCategoryId = selected?.id;

    try {
      if (!isNew) {
        const response = await updateCategories(savedCategoryId, payload);
        if (!silent) showToast(response?.data?.message, "success");
        await fetchData(savedCategoryId, { staleIfSelectionChangedFrom: savedCategoryId });
      } else {
        // Carried into the undo/redo snapshot too -- otherwise redoing this
        // add (or undoing its delete) re-creates the category with no
        // position hint and it gets appended to the end of the list instead
        // of restored where it was.
        const createPayload = {
          ...payload,
          insert_after_id: selected?.insertAfterId || undefined,
        };
        const response = await createCategories(activeSectionId, createPayload);
        const newCategory = response?.data?.data;
        if (selected?.draftKey && newCategory?.id) {
          savedDraftIdsRef.current[selected.draftKey] = newCategory.id;
          if (genreAddedRef.current[selected.draftKey]) {
            genreAddedRef.current[newCategory.id] = genreAddedRef.current[selected.draftKey];
          }
        }
        if (!silent) showToast(response?.data?.message, "success");
        await fetchData(newCategory?.id, { staleIfSelectionChangedFrom: savedCategoryId });
        pushHistory({
          type: "category-add",
          categoryId: newCategory?.id,
          sectionId: activeSectionId,
          snapshot: createPayload,
        });
      }
      return true;
    } catch (err) {
      showToast(
        err?.response?.data?.message || err?.message || "Error saving category",
        "error",
      );
      return false;
    } finally {
      savingRef.current.delete(savingKey);
    }
  };

  // Autosave: once an edit lands (`dirty`), wait for a pause in typing and
  // persist it in the background. Safe on a live page -- the backend marks
  // every saved category edit as a draft (SectionCategory.is_active: false),
  // hidden from the website until the page's Publish action republishes it.
  // Saving is also what expands picked genres into their titles (the server
  // fills all_contents), so the Selected Content list fills in right after.
  useEffect(() => {
    if (!dirty || !selected) return undefined;
    const timer = setTimeout(() => {
      persistCategory({ silent: true });
    }, 1500);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [postFormData, dirty, selected]);

  const getGenresList = async () => {
    try {
      const response = await GenresList();
      setGenres(response?.data?.data || []);
    } catch (err) {
      showToast(err?.response?.data?.message || "Error loading genres", "error");
    }
  };

  const loadTheme = async () => {
    // Always fetch GET /config/theme: it returns the working draft to an admin.
    // GET /config (AppConfigContext) now serves the last-published theme, so
    // reusing it here would show published colors instead of unpublished edits.
    try {
      const response = await getTheme();
      setTheme(response.data?.data || null);
    } catch (err) {
      showToast(err?.response?.data?.message || "Error loading theme", "error");
    }
  };

  const fetchWidgetTypes = async () => {
    try {
      const response = await getWidgetTypes();
      // Hides the "CUSTOM - landscape" widget the backend still returns --
      // there's no dedicated canvas preview for it, so it's kept out of the
      // library rather than removed at the source. "Continue Watching" and
      // "Recently Added" are hidden the same way -- pulled from the picker
      // without touching the backend's widget-types data. Matched on `value`
      // (the row's stable slug, persisted as layout_type) rather than
      // label/heading, which are just display text an admin could rename.
      const HIDDEN_WIDGET_VALUES = ["landscape", "continue-watching", "recently-added"];
      const rows = (response.data?.data || []).filter(
        (row) => !HIDDEN_WIDGET_VALUES.includes(row.value)
      );
      setWidgetGroups(groupWidgetRows(rows));
    } catch (err) {
      showToast(err?.response?.data?.message || "Error loading widgets library", "error");
    }
  };

  useEffect(() => {
    // HashRouter doesn't reset scroll on navigation, so landing here after
    // scrolling down on a previous page hides the top toolbar below the fold.
    window.scrollTo(0, 0);
    fetchSectionsList({ preferredId: state?.sectionId });
    getGenresList();
    loadTheme();
    fetchWidgetTypes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The Theme tab's font display (and the canvas's --preview-font) only
  // ever render the current family, so load just that one instead of every
  // font this CMS could show, and swap it when the family changes.
  useEffect(() => {
    if (!theme?.font_family) return undefined;
    const FONT_STYLESHEET_ID = "home-builder-theme-fonts";
    document.getElementById(FONT_STYLESHEET_ID)?.remove();
    const link = document.createElement("link");
    link.id = FONT_STYLESHEET_ID;
    link.rel = "stylesheet";
    link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(theme.font_family)}:wght@400;500;600;700&display=swap`;
    document.head.appendChild(link);
    return () => {
      link.parentNode?.removeChild(link);
    };
  }, [theme?.font_family]);

  // Typography editing was removed from the Theme tab -- Poppins is now the
  // one fixed brand font -- so correct any theme still saved with a
  // different family instead of leaving it stuck on whatever was picked
  // before the control was taken away.
  useEffect(() => {
    if (canEditTheme && theme && theme.font_family !== "Poppins") {
      patchTheme({ font_family: "Poppins" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [theme?.font_family]);

  useEffect(() => {
    if (activeSectionId) {
      setSelected(null);
      setPostFormData({
        name: "",
        layout_type: "",
        genres: [],
        contents: [],
        videos: [],
        contents_payload: [],
        config: DEFAULT_CATEGORY_CONFIG,
      });
      // syncPanel: false -- categories still populate the canvas/Layers list,
      // but nothing is auto-selected; the admin picks a section explicitly.
      fetchData(undefined, { syncPanel: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSectionId]);

  const handleSelectPage = async (id) => {
    if (id === activeSectionId) return;
    if (!(await confirmDirtySwitch())) return;
    setActiveSectionId(id);
  };

  const openAddPage = () => {
    if (!canCreate) {
      showToast("You don't have permission to add pages", "error");
      return;
    }
    setEditingSection(null);
    setSectionModalOpen(true);
  };

  // Top-bar "+" tab: skips the naming modal entirely -- creates a draft page
  // right away (de-duped against existing names, since the backend rejects
  // duplicates case-insensitively) and activates it. The admin lands on the
  // empty-page state and can open the Templates picker from there if wanted.
  const handleQuickAddPage = async () => {
    if (!canCreate) {
      showToast("You don't have permission to add pages", "error");
      return;
    }
    const existingNames = new Set(sections.map((s) => (s.name || "").trim().toLowerCase()));
    let name = "New Page";
    let suffix = 2;
    while (existingNames.has(name.toLowerCase())) {
      name = `New Page ${suffix}`;
      suffix += 1;
    }
    try {
      const response = await createSections({ name, is_active: false });
      await fetchSectionsList({ selectNewest: true });
      pushHistory({ type: "page-add", pageId: response?.data?.data?.id, name, is_active: false });
    } catch (err) {
      showToast(err?.response?.data?.message || "Error creating page", "error");
    }
  };

  const openEditPage = (section) => {
    if (!canEdit) {
      showToast("You don't have permission to edit pages", "error");
      return;
    }
    setEditingSection(section);
    setSectionModalOpen(true);
  };

  // Selecting a template from the picker: if the page already has sections,
  // route through the ConfirmModal below (consistent with the rest of the
  // app's confirmations) instead of a native window.confirm before
  // destroying them.
  const handleSelectTemplate = (template) => {
    if (categories.length > 0) {
      setTemplatesOpen(false);
      setPendingTemplate(template);
      return;
    }
    applyTemplate(template);
  };

  const applyTemplate = async (template) => {
    if (!canCreate) {
      showToast("You don't have permission to add sections", "error");
      return;
    }
    if (!activeSectionId) {
      showToast("Create or select a page first", "error");
      return;
    }
    setTemplatesOpen(false);
    const oldCategoryIds = categories.filter((c) => c.id).map((c) => c.id);
    try {
      // Create every starter category first and only remove the old ones
      // once all of them exist -- deleting first would leave the page
      // destroyed with no undo if a create call failed partway through.
      for (const starter of template.starterCategories || []) {
        await createCategories(activeSectionId, {
          name: starter.name,
          layout_type: starter.layout_type,
          genres: [],
          contents: [],
          config: starter.config || withConfigDefaults({}, starter.layout_type),
        });
      }
      await Promise.all(oldCategoryIds.map((id) => deleteCategoriesns(id)));
      showToast("Template applied", "success");
      await fetchSectionsList({ preferredId: activeSectionId });
      await fetchData();
      setLeftTab("widgets");
    } catch (err) {
      showToast(err?.response?.data?.message || "Error applying template", "error");
      // Whatever mix of old/new categories exists on the server at this
      // point, reflect it -- don't leave the panel showing pre-failure state.
      await fetchSectionsList({ preferredId: activeSectionId });
      await fetchData();
    }
  };

  const handleDeletePage = (id) => {
    if (!canDelete) {
      showToast("You don't have permission to delete pages", "error");
      return;
    }
    setPendingDeletePage(id);
  };

  const confirmDeletePage = async () => {
    const id = pendingDeletePage;
    setPendingDeletePage(null);
    try {
      const response = await deleteSections(id);
      if (response?.data?.status === "success") {
        showToast(response?.data?.message || "Page deleted Successfully", "success");
      }
    } catch (err) {
      showToast(err?.response?.data?.message, "error");
    } finally {
      fetchSectionsList();
    }
  };

  const handleTogglePageVisibility = async (sec) => {
    if (!canEdit) {
      showToast("You don't have permission to edit pages", "error");
      return;
    }
    const nextActive = !sec.is_active;
    try {
      await updateSections(sec.id, { is_active: nextActive });
      setSections((prev) =>
        prev.map((s) => (s.id === sec.id ? { ...s, is_active: nextActive } : s))
      );
      showToast(
        nextActive
          ? "Page will show in navigation once published"
          : "Page will be hidden from navigation once published",
        "success"
      );
    } catch (err) {
      showToast(err?.response?.data?.message || "Error updating page", "error");
    }
  };

  // Copies a page's name (as "<name> copy", de-duped) plus every one of its
  // categories -- mirrors applyTemplate's create-then-select pattern so a
  // failed category create partway through still leaves the new page usable.
  const handleDuplicatePage = async (sec) => {
    if (!canCreate) {
      showToast("You don't have permission to add pages", "error");
      return;
    }
    const existingNames = new Set(sections.map((s) => (s.name || "").trim().toLowerCase()));
    let name = `${sec.name} copy`;
    let suffix = 2;
    while (existingNames.has(name.toLowerCase())) {
      name = `${sec.name} copy ${suffix}`;
      suffix += 1;
    }
    try {
      const response = await createSections({ name, is_active: false });
      const newId = response?.data?.data?.id;
      for (const cat of sec.categories || []) {
        await createCategories(newId, {
          name: cat.name,
          layout_type: cat.layout_type,
          genres: (cat.genres || []).map((g) => (typeof g === "object" ? g.id : g)),
          contents: (cat.contents_data || []).map((v) => v.id),
          config: cat.config,
        });
      }
      showToast("Page duplicated", "success");
      await fetchSectionsList({ preferredId: newId });
    } catch (err) {
      showToast(err?.response?.data?.message || "Error duplicating page", "error");
    }
  };

  const handleSetHomePage = async (sec) => {
    if (!canEdit) {
      showToast("You don't have permission to edit pages", "error");
      return;
    }
    if (sec.is_home) return;
    try {
      await updateSections(sec.id, { is_home: true });
      setSections((prev) => prev.map((s) => ({ ...s, is_home: s.id === sec.id })));
      showToast("Set as home page — publish to update the site", "success");
    } catch (err) {
      showToast(err?.response?.data?.message || "Error updating page", "error");
    }
  };

  const handleDeleteCategory = (cat) => {
    if (!canDelete) {
      showToast("You don't have permission to delete sections", "error");
      return;
    }
    setPendingDeleteCategory(cat);
  };

  const confirmDeleteCategory = async () => {
    const cat = pendingDeleteCategory;
    setPendingDeleteCategory(null);

    if (!cat.id) {
      // Never persisted -- just drop the local draft.
      setCategories((prev) => prev.filter((c) => c !== cat));
      setSelected((prev) => (prev === cat ? null : prev));
      return;
    }

    try {
      const response = await deleteCategoriesns(cat.id);
      if (response?.data?.status === "success") {
        showToast(response?.data?.message, "success");
      }
      // The neighbor still sitting where `cat` used to be -- undoing this
      // delete recreates `cat` right after it, instead of appending it to
      // the end of the list.
      const idx = categories.findIndex((c) => c.id === cat.id);
      const previousNeighborId = idx > 0 ? categories[idx - 1].id : undefined;
      pushHistory({
        type: "category-delete",
        categoryId: cat.id,
        sectionId: activeSectionId,
        snapshot: {
          name: cat.name,
          layout_type: cat.layout_type,
          genres: (cat.genres || []).map((g) => (typeof g === "object" ? g.id : g)),
          contents: cat.contents || [],
          config: cat.config,
          insert_after_id: previousNeighborId,
        },
      });
    } catch (err) {
      const error = err?.response?.data?.message;
      showToast(error, "error");
    } finally {
      // Deleting some other category shouldn't wipe whatever is half-typed
      // for the one currently open -- unless it's that very category.
      fetchData(undefined, { syncPanel: !dirty || cat.id === selected?.id });
    }
  };

  const moveCategory = async (direction) => {
    if (!canEdit || !selected?.id) return;
    const idx = categories.findIndex((c) => c.id === selected.id);
    const targetIdx = direction === "up" ? idx - 1 : idx + 1;
    if (idx === -1 || targetIdx < 0 || targetIdx >= categories.length) return;
    const firstId = selected.id;
    const secondId = categories[targetIdx].id;
    try {
      const response = await postSwapOrderCategoyiesList({
        first_id: firstId,
        second_id: secondId,
      });
      showToast(response?.data?.message, "success");
      pushHistory({ type: "category-reorder", firstId, secondId, sectionId: activeSectionId });
    } catch (err) {
      showToast(err?.response?.data?.message, "error");
    } finally {
      fetchData(selected.id);
    }
  };

  // Defaults to the currently selected/edited category (using its live-edited
  // form state) when called from the Section Settings header; the Layers
  // panel's right-click menu instead passes an arbitrary row's own `cat`, so
  // it reads straight off that category rather than `postFormData`, since it
  // may not be the one currently open for editing.
  const duplicateCategory = async (catArg) => {
    const sourceId = catArg ? catArg.id : selected?.id;
    if (!canCreate || !sourceId) return;
    const source = catArg
      ? {
        name: catArg.name,
        layout_type: catArg.layout_type,
        genres: (catArg.genres || []).map((g) => (typeof g === "object" ? g.id : g)),
        contents: (catArg.contents_data || []).map((v) => v.id),
        config: catArg.config,
      }
      : {
        name: postFormData.name,
        layout_type: postFormData.layout_type,
        genres: (postFormData.genres || []).map((g) => g.id),
        contents: (postFormData.videos || []).map((v) => v.id),
        config: postFormData.config,
      };
    try {
      // Placed right after the source category (both on the server and in
      // the undo/redo snapshot) -- otherwise the duplicate, and any later
      // redo/undo-recreate of it, gets appended to the end of the list
      // instead of sitting next to the category it was copied from.
      const payload = { ...source, name: uniqueCategoryName(source.name), insert_after_id: sourceId };
      const response = await createCategories(activeSectionId, payload);
      showToast("Category duplicated", "success");
      await fetchData(response?.data?.data?.id);
      pushHistory({
        type: "category-add",
        categoryId: response?.data?.data?.id,
        sectionId: activeSectionId,
        snapshot: payload,
      });
    } catch (err) {
      showToast(err?.response?.data?.message || "Error duplicating category", "error");
    }
  };

  // Bubbles `cat` up to the top of the list by repeatedly swapping its sort
  // order with each earlier row, closest neighbor first -- there's no
  // dedicated "move to position" endpoint, only pairwise swap-by-id.
  const moveCategoryToTop = async (cat) => {
    if (!canEdit || !cat?.id) return;
    const idx = categories.findIndex((c) => c.id === cat.id);
    if (idx <= 0) return;
    // Recorded as one "category-move" command carrying every swap, instead
    // of one "category-reorder" entry per swap -- otherwise a single Undo
    // only reverses the last pairwise swap, leaving the category stuck
    // partway back down the list instead of restoring its original spot.
    const swaps = [];
    try {
      for (let i = idx; i > 0; i--) {
        const firstId = cat.id;
        const secondId = categories[i - 1].id;
        await postSwapOrderCategoyiesList({ first_id: firstId, second_id: secondId });
        swaps.push({ firstId, secondId });
      }
      pushHistory({ type: "category-move", swaps, sectionId: activeSectionId });
      showToast("Moved to top", "success");
    } catch (err) {
      showToast(err?.response?.data?.message, "error");
    } finally {
      fetchData(cat.id);
    }
  };

  const handleCategoryDragStart = (cat) => {
    setDragCat(cat);
  };

  const handleCategoryDragEnter = (cat) => {
    setDragOverCat(cat);
  };

  // Auto-scrolls the Layers/Widgets panel while a row is being dragged near
  // its top/bottom edge -- native HTML5 drag doesn't reliably auto-scroll a
  // nested overflow container, so we drive it ourselves off the pointer position.
  const dragAutoScrollTick = () => {
    const el = sidebarRef.current;
    const y = dragScrollY.current;
    if (el && y != null) {
      const rect = el.getBoundingClientRect();
      const EDGE = 56;
      const MAX_SPEED = 16;
      const topGap = y - rect.top;
      const bottomGap = rect.bottom - y;
      let speed = 0;
      if (topGap < EDGE) {
        speed = -MAX_SPEED * (1 - Math.max(0, topGap) / EDGE);
      } else if (bottomGap < EDGE) {
        speed = MAX_SPEED * (1 - Math.max(0, bottomGap) / EDGE);
      }
      if (speed) el.scrollTop += speed;
    }
    dragScrollFrame.current = requestAnimationFrame(dragAutoScrollTick);
  };

  const handleSidebarDragOver = (e) => {
    if (!dragCat && !dragPageIdRef.current) return;
    dragScrollY.current = e.clientY;
    if (!dragScrollFrame.current) {
      dragScrollFrame.current = requestAnimationFrame(dragAutoScrollTick);
    }
  };

  const stopDragAutoScroll = () => {
    if (dragScrollFrame.current) {
      cancelAnimationFrame(dragScrollFrame.current);
      dragScrollFrame.current = null;
    }
    dragScrollY.current = null;
  };

  // Safety net for the auto-scroll loop above: it's normally stopped by the
  // dragged row's own onDragEnd, but a mid-drag refetch (several actions
  // trigger one) can unmount that exact row first, in which case its
  // onDragEnd never fires and the rAF loop would run forever. A
  // document-level listener isn't tied to that row's lifetime, so it still
  // catches the browser's dragend/drop regardless.
  useEffect(() => {
    document.addEventListener("dragend", stopDragAutoScroll);
    document.addEventListener("drop", stopDragAutoScroll);
    return () => {
      document.removeEventListener("dragend", stopDragAutoScroll);
      document.removeEventListener("drop", stopDragAutoScroll);
    };
  }, []);

  const handlePageDragEnter = (targetId) => {
    const fromId = dragPageIdRef.current;
    if (!fromId || fromId === targetId) return;
    const ids = orderedSections.map((s) => s.id);
    const to = ids.indexOf(targetId);
    ids.splice(ids.indexOf(fromId), 1);
    ids.splice(to, 0, fromId);
    setPageOrder(ids);
  };

  const handlePageDragEnd = () => {
    stopDragAutoScroll();
    dragPageIdRef.current = null;
    // Dropped back into the published order -- nothing left to publish.
    setPageOrder((prev) => (prev && prev.every((id, i) => sections[i]?.id === id) ? null : prev));
  };

  // Saves the drafted page order on publish. sections[] arrives sorted by
  // sort_order, so swapping sort_order pairwise (selection-sort style) walks
  // the server into the drafted order using the existing swap endpoint.
  const persistPageOrder = async () => {
    if (!pageOrder) return true;
    const working = sections.map((s) => s.id);
    const target = orderedSections.map((s) => s.id);
    try {
      for (let i = 0; i < target.length; i++) {
        if (working[i] === target[i]) continue;
        const j = working.indexOf(target[i]);
        await swapOrder({ first_id: working[i], second_id: target[i] });
        [working[i], working[j]] = [working[j], working[i]];
      }
      sectionsRef.current = orderedSections;
      setSections(orderedSections);
      setPageOrder(null);
      return true;
    } catch (err) {
      showToast(err?.response?.data?.message || "Error saving page order", "error");
      // Resync with whatever swaps did land; the draft order is kept for a retry.
      fetchSectionsList();
      return false;
    }
  };

  const handleCategoryDragEnd = async () => {
    stopDragAutoScroll();
    // A never-persisted draft (id: "") is a valid drag source/target in the
    // DOM but has nothing on the server to swap sort_order with.
    if (!dragCat?.id || !dragOverCat?.id) {
      setDragCat(null);
      setDragOverCat(null);
      return;
    }
    const firstId = dragCat.id;
    const secondId = dragOverCat.id;
    try {
      const response = await postSwapOrderCategoyiesList({
        first_id: firstId,
        second_id: secondId,
      });
      showToast(response?.data?.message, "success");
      pushHistory({ type: "category-reorder", firstId, secondId, sectionId: activeSectionId });
    } catch (err) {
      showToast(err?.response?.data?.message, "error");
    } finally {
      // Reordering two other categories shouldn't wipe whatever is
      // half-typed for the one currently open -- unless it's one of the two.
      const touchesSelected =
        selected && (dragCat?.id === selected.id || dragOverCat?.id === selected.id);
      fetchData(selected?.id, { syncPanel: !dirty || touchesSelected });
    }
    setDragCat(null);
    setDragOverCat(null);
  };

  const updateCardDesign = (field, value) => {
    setPostFormData((prev) => ({
      ...prev,
      config: {
        ...prev.config,
        cardDesign: { ...prev.config.cardDesign, [field]: value },
      },
    }));
    setDirty(true);
  };

  const updateBehaviour = (field, value) => {
    setPostFormData((prev) => ({
      ...prev,
      config: {
        ...prev.config,
        behaviour: { ...DEFAULT_BEHAVIOUR, ...prev.config?.behaviour, [field]: value },
      },
    }));
    setDirty(true);
  };
  const itemCount = postFormData?.videos?.length || 0;
  const selectedIsBanner = BANNER_LAYOUT_TYPES.includes(postFormData?.layout_type);
  // Rails: only when the cards actually overflow the canvas row (measured).
  // Banners show one slide at a time, so any second slide is worth sliding to.
  const canAutoScroll = selectedIsBanner ? itemCount >= 2 : itemCount > 0 && selectedRailOverflows;
  const autoScrollHint = selectedIsBanner
    ? "Add at least 2 items to auto scroll the banner"
    : "All cards already fit in the row, so there's nothing to scroll";
  const canShuffle = itemCount >= SHUFFLE_MIN_ITEMS;
  const activeBehaviour = effectiveBehaviour(postFormData?.config?.behaviour, itemCount);
  const autoScrollOn = canAutoScroll && activeBehaviour.autoScroll;

  // Left empty mid-edit instead of snapping straight to CARDS_VISIBLE_MIN --
  // clamping on every keystroke made it impossible to clear the field and
  // type a fresh number (e.g. backspacing "6" then typing "25" clamped to
  // "1" after the backspace, so the "25" landed as "1" then "125"->clamped).
  // commitCardsVisible fills in the minimum on blur if it's still empty.
  const updateCardsVisible = (deviceKey, value) => {
    if (value === "") {
      setPostFormData((prev) => ({
        ...prev,
        config: {
          ...prev.config,
          cardsVisible: { ...prev.config.cardsVisible, [deviceKey]: "" },
        },
      }));
      setDirty(true);
      return;
    }
    const parsed = parseInt(value, 10);
    if (Number.isNaN(parsed)) return;
    const clamped = Math.min(CARDS_VISIBLE_MAX, Math.max(CARDS_VISIBLE_MIN, parsed));
    setPostFormData((prev) => ({
      ...prev,
      config: {
        ...prev.config,
        cardsVisible: { ...prev.config.cardsVisible, [deviceKey]: clamped },
      },
    }));
    setDirty(true);
  };

  const commitCardsVisible = (deviceKey) => {
    setPostFormData((prev) => {
      if (prev.config?.cardsVisible?.[deviceKey] !== "") return prev;
      return {
        ...prev,
        config: {
          ...prev.config,
          cardsVisible: { ...prev.config.cardsVisible, [deviceKey]: CARDS_VISIBLE_MIN },
        },
      };
    });
  };

  const setDataSource = (value) => {
    setPostFormData((prev) => ({
      ...prev,
      config: { ...prev.config, dataSource: value },
    }));
    setDirty(true);
  };

  const themeUpdateTimer = useRef(null);
  const pendingThemePatch = useRef({});
  // Shared by patchTheme and the undo/redo "theme-patch" command -- applies a
  // patch to local state and (debounced) to the backend, without itself
  // touching the history stack, so replaying a command on undo/redo doesn't
  // push a new entry on top of itself.
  const applyThemePatch = (patch) => {
    setTheme((prev) => ({ ...prev, ...patch }));
    // Accumulate across debounce restarts -- a second change within 500ms of
    // the first must not replace it, or the first one is silently never sent.
    pendingThemePatch.current = { ...pendingThemePatch.current, ...patch };
    if (themeUpdateTimer.current) clearTimeout(themeUpdateTimer.current);
    themeUpdateTimer.current = setTimeout(async () => {
      const toSend = pendingThemePatch.current;
      pendingThemePatch.current = {};
      try {
        await updateTheme(toSend);
        // AppConfigContext fetches GET /config once per session and
        // loadTheme() reuses that snapshot -- without invalidating it here,
        // leaving this page and coming back would show the pre-edit theme.
        appConfig.refreshConfig();
      } catch (err) {
        showToast(err?.response?.data?.message || "Error saving theme", "error");
      }
    }, 500);
  };

  const patchTheme = (patch) => {
    if (!canEditTheme) {
      showToast("You don't have permission to edit theme settings", "error");
      return;
    }
    // Snapshot the pre-change value of every field this patch touches so
    // undo can restore it -- taken from live `theme` state, not the patch,
    // since the patch only carries the *new* values.
    const previous = {};
    Object.keys(patch).forEach((key) => {
      previous[key] = theme?.[key];
    });
    pushHistory({ type: "theme-patch", previous, patch });
    applyThemePatch(patch);
  };

  const HEX_COLOR_RE = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i;

  // Hex comparisons for swatch "active" highlighting -- the backend
  // normalizes saved colors to lowercase, but preset lists (THEME_ACCENT_PRESETS,
  // THEME_BACKGROUND_PRESETS) are written in mixed case, so a strict === would
  // never match a preset even when it's the one actually applied.
  const isSameColor = (a, b) => !!a && !!b && a.toLowerCase() === b.toLowerCase();

  // Shared by the accent-color and app-background custom swatch pickers --
  // both are just "validate a hex value, add/remove it via the theme API,
  // refresh theme state" with a different API pair, error setter, picker-open
  // setter, and label for the fallback toast message.
  const makeSwatchHandlers = (addApi, removeApi, setError, setPickerOpen, entityLabel, invalidMessage, { onAdded, onRemoved } = {}) => ({
    add: async (rawColor) => {
      if (!canEditTheme) {
        showToast("You don't have permission to edit theme settings", "error");
        return;
      }
      const color = (rawColor || "").trim();
      if (!HEX_COLOR_RE.test(color)) {
        setError(invalidMessage);
        return;
      }
      setError("");
      try {
        const response = await addApi(color);
        setTheme(response.data?.data || null);
        setPickerOpen(false);
        onAdded?.(color);
        appConfig.refreshConfig();
      } catch (err) {
        showToast(err?.response?.data?.message || `Error adding ${entityLabel}`, "error");
      }
    },
    remove: async (color, e) => {
      e.stopPropagation();
      if (!canEditTheme) {
        showToast("You don't have permission to edit theme settings", "error");
        return;
      }
      try {
        const response = await removeApi(color);
        setTheme(response.data?.data || null);
        onRemoved?.(color);
        appConfig.refreshConfig();
      } catch (err) {
        showToast(err?.response?.data?.message || `Error removing ${entityLabel}`, "error");
      }
    },
  });

  const { add: addAccentColor, remove: removeAccentColor } = makeSwatchHandlers(
    addCustomAccentColor,
    removeCustomAccentColor,
    setCustomColorError,
    setAccentPickerOpen,
    "color",
    "Enter a valid hex color, e.g. #3E7BFA"
  );

  const { add: addBackgroundColor, remove: removeBackgroundColor } = makeSwatchHandlers(
    addCustomBackground,
    removeCustomBackground,
    setCustomBgError,
    setBgPickerOpen,
    "background",
    "Enter a valid hex color, e.g. #1A1A1A"
  );

  const { add: addFontColor, remove: removeFontColor } = makeSwatchHandlers(
    addCustomFontColor,
    removeCustomFontColor,
    setCustomFontError,
    setFontPickerOpen,
    "font color",
    "Enter a valid hex color, e.g. #FFFFFF",
    {
      onAdded: setDraftFontColor,
      onRemoved: (color) => setDraftFontColor((prev) => (isSameColor(prev, color) ? null : prev)),
    }
  );

  const selectFontColor = (color) => {
    if (!canEditTheme) {
      showToast("You don't have permission to edit theme settings", "error");
      return;
    }
    setDraftFontColor(isSameColor(color, theme?.font_color) ? null : color);
  };

  // Saves the drafted font color on publish -- the only point it goes live.
  const persistDraftFontColor = async () => {
    if (!draftFontColor) return true;
    try {
      await updateTheme({ font_color: draftFontColor });
      setTheme((prev) => ({ ...prev, font_color: draftFontColor }));
      setDraftFontColor(null);
      appConfig.refreshConfig();
      return true;
    } catch (err) {
      showToast(err?.response?.data?.message || "Error saving font color", "error");
      return false;
    }
  };

  const widgets = widgetGroups.flatMap((g) => g.items);
  const currentSection = sections.find((s) => s.id === activeSectionId);
  // Pages panel order: the unpublished drag draft if any, else server order.
  // Pages missing from the draft (created since) keep their place at the end.
  const orderedSections = pageOrder
    ? [...sections].sort((a, b) => {
        const ia = pageOrder.indexOf(a.id);
        const ib = pageOrder.indexOf(b.id);
        return (ia < 0 ? pageOrder.length : ia) - (ib < 0 ? pageOrder.length : ib);
      })
    : sections;
  const isAlgorithmic = ALGORITHMIC_DATA_SOURCES.includes(postFormData.config?.dataSource);
  // Algorithmic rows are filled server-side, so their card count isn't known
  // here -- the website still enforces the minimum for them at render time.
  const scrollBehaviourLocked =
    !isAlgorithmic && isScrollBehaviourLocked(postFormData.layout_type, postFormData.videos?.length || 0);
  const isPlaceholderSource = PLACEHOLDER_DATA_SOURCES.includes(postFormData.config?.dataSource);
  const devicePreset = DEVICE_PRESETS[device];
  const selectedCategoryIndex = categories.findIndex((c) => c.id === selected?.id);
  // Cosmetic only -- the address bar text in the Web canvas frame's browser
  // chrome, not a real link.
  const browserDomain = `${(process.env.REACT_APP_TITLE || "site").toLowerCase().replace(/\s+/g, "")}.com`;


  const fontColor = draftFontColor || theme?.font_color;
  const themeVars = theme
    ? {
      "--preview-accent": theme.accent_color,
      // The backend rejects anything but a resolved hex color for
      // app_background (so every client, including ones that don't know
      // this CMS's preset table, can consume it directly as CSS) -- it's
      // never a preset's semantic `value` like "charcoal".
      "--preview-bg": HEX_COLOR_RE.test(theme.app_background) ? theme.app_background : "#141414",
      "--preview-radius": `${theme.corner_radius}px`,
      "--preview-font": theme.font_family,
      ...(HEX_COLOR_RE.test(fontColor) && { "--preview-text": fontColor }),
    }
    : {};

  const categoryListMarkup = (
    <div className={styles.categoryList}>
      {categories?.map((cat) => {
        const widget = widgets.find((w) => w.value === cat.layout_type);
        const itemCount = cat.contents_data?.length ?? cat.items ?? 0;
        return (
          <div
            key={cat.id || "draft"}
            className={`${styles.categoryItem} ${selected?.id === cat.id ? styles.categorySelected : ""}`}
            onClick={() => handleLayerClick(cat)}
            onContextMenu={(e) => {
              e.preventDefault();
              setLayerMenu({ cat, x: e.clientX, y: e.clientY });
            }}
            draggable={!!cat.id}
            onDragStart={() => handleCategoryDragStart(cat)}
            onDragEnter={() => handleCategoryDragEnter(cat)}
            onDragEnd={handleCategoryDragEnd}
            onDragOver={(e) => e.preventDefault()}
          >
            <span className={styles.categoryDragHandle} title="Drag to reorder">
              <TbGripVertical />
            </span>
            <span className={styles.categoryLayoutIcon}>
              {widget?.icon || <IoAlbumsOutline />}
            </span>

            <div className={styles.categoryTextBlock}>
              <p className={styles.categoryName}>{cat.name || "Untitled"}</p>
              <p className={styles.categoryInfo}>
                {itemCount} items • {widget?.description || widget?.label || cat.layout_type}
              </p>
            </div>
          </div>

        );
      })}
    </div>
  );

  // Renders one category's preview block -- a banner, an empty/sample-data
  // notice, or a header + rail of cards -- for the stacked canvas below.
  // `vm` is either the live-edited `postFormData` (for the selected category,
  // so in-progress edits show immediately) or a plain view built from that
  // category's own last-saved data (everyone else in the stack).
  const renderCategoryPreview = (vm, { isSelected, categoryId, reportOverflow } = {}) => {
    const vmIsBanner = BANNER_LAYOUT_TYPES.includes(vm.layout_type);
    const vmIsAlgorithmic = ALGORITHMIC_DATA_SOURCES.includes(vm.config?.dataSource);
    const vmRailClass = styles[RAIL_CLASS_KEY[vm.layout_type]] || styles.railCarousel;
    const behaviour = effectiveBehaviour(vm.config?.behaviour, vm.videos?.length || 0);

    // Shuffle wins over an explicit sort (they're mutually exclusive controls
    // in the UI); "manual" sorting leaves the admin's own ordering untouched.
    let displayVideos = vm.videos || [];
    if (behaviour.shuffleOrder) {
      displayVideos = seededShuffle(displayVideos, categoryId || 1);
    } else if (behaviour.sorting === "alphabetical") {
      displayVideos = [...displayVideos].sort((a, b) =>
        (a.title || a.name || "").localeCompare(b.title || b.name || "")
      );
    } else if (behaviour.sorting === "newest") {
      displayVideos = [...displayVideos].sort(
        (a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0)
      );
    } else if (behaviour.sorting === "oldest") {
      displayVideos = [...displayVideos].sort(
        (a, b) => new Date(a.created_at || 0) - new Date(b.created_at || 0)
      );
    }

    if (!displayVideos.length) {
      return (
        <div className={styles.canvasEmpty}>
          {vmIsAlgorithmic ? "No content available for this source yet" : "No content in this category yet"}
        </div>
      );
    }
    if (vmIsBanner) {
      const cardDesign = vm.config?.cardDesign || DEFAULT_CARD_DESIGN;
      return (
        <BannerCarousel
          items={displayVideos}
          name={vm.name}
          cardDesign={cardDesign}
          autoScroll={behaviour.autoScroll}
          infiniteScroll={behaviour.infiniteScroll}
          landscapeOverrideItems={landscapeOverrideItems}
          setLandscapeOverrideItems={setLandscapeOverrideItems}
        />
      );
    }

    // Auto scroll: a continuous CSS marquee on a duplicated item track, so it
    // loops seamlessly. Without "infinite scroll" it still uses the doubled
    // track (for a clean stopping point) but plays once and holds instead of
    // repeating. RailViewport only turns it on when the cards overflow the
    // row; otherwise items render directly with normal horizontal scrolling.
    return (
      <>
        <div className={styles.railHeader}>
          <h3 className={styles.railHeaderTitle}>
            {truncateByLetters(vm.name, 50)}
          </h3>
          {vm.config?.dataSource && vm.config.dataSource !== "manual" && (
            <span className={styles.railHeaderTag}>
              {DATA_SOURCE_OPTIONS.find((opt) => opt.value === vm.config?.dataSource)?.label ||
                vm.config?.dataSource}
            </span>
          )}
          {displayVideos.length > 20 && websiteUrl && (
            <button
              type="button"
              className={styles.railViewAllBtn}
              onClick={(e) => {
                e.stopPropagation();
                window.open(websiteUrl, "_blank", "noopener,noreferrer");
              }}
            >
              View All
            </button>
          )}
        </div>
        <RailViewport
          className={`${styles.rail} ${vmRailClass}`}
          itemCount={displayVideos.length}
          autoScroll={behaviour.autoScroll}
          infiniteScroll={behaviour.infiniteScroll}
          onOverflowChange={reportOverflow ? setSelectedRailOverflows : undefined}
          renderCards={(doubled) => {
            const railItems = doubled ? [...displayVideos, ...displayVideos] : displayVideos;
            const cards = railItems.map((item, index) => {
              const cardDesign = vm.config?.cardDesign || DEFAULT_CARD_DESIGN;

              const effectiveRatio = getEffectiveImageRatio(vm.layout_type, cardDesign);

              const src =
                vm.layout_type === "row-2x3"
                  ? item.portrait || item.thumbnail
                  : item.thumbnail || item.portrait;
              // Detected from the image's real pixel dimensions once it
              // loads (see onLoad below), not guessed from which field
              // (portrait vs thumbnail) it came from -- an admin can upload
              // a landscape image straight into a "portrait"/"square" slot,
              // which a field-based guess can't catch.
              const isFixedShapeBox = effectiveRatio === "2:3" || effectiveRatio === "1:1";
              const usingLandscapeInFixedBox = isFixedShapeBox && landscapeOverrideItems.has(item.id);
              // Corner radius itself is applied via CSS (the layout-specific
              // .railXxx .railThumb rules read the live --preview-radius
              // custom property set on .canvasFrame) so it repaints the
              // instant the Theme Builder slider changes, with no dependency
              // on this component re-rendering. Circle cards stay fully
              // round regardless -- the slider doesn't apply to them.
              const cardRadius = vm.layout_type === "circle" ? "50%" : "var(--preview-radius, 10px)";
              const thumbStyle = {
                aspectRatio: ratioToCss(effectiveRatio),
                borderRadius: cardRadius,
                boxShadow: cardDesign.dropShadow ? "0 6px 16px rgba(0, 0, 0, 0.35)" : "none",
                ...(usingLandscapeInFixedBox
                  ? { objectFit: "contain", objectPosition: "center", backgroundColor: "#000" }
                  : {}),
              };
              const showTvFocusDemo = isSelected && device === "tv" && index === 0 && theme;
              const tvFocusClass = showTvFocusDemo ? TV_FOCUS_CLASS[theme.focus_effect] : "";
              // Repeats 1..N once per pass through the doubled auto-scroll track.
              const rank = (index % displayVideos.length) + 1;
              return (
                <div key={`${item.id}-${index}`} className={`${styles.railItem} ${tvFocusClass}`}>
                  <div className={styles.railThumbWrap}>
                    <img
                      src={src}
                      className={styles.railThumb}
                      style={thumbStyle}
                      alt=""
                      onLoad={(e) => {
                        if (!isFixedShapeBox) return;
                        const img = e.currentTarget;
                        const isActuallyLandscape = img.naturalWidth > img.naturalHeight;
                        setLandscapeOverrideItems((prev) => {
                          const already = prev.has(item.id);
                          if (isActuallyLandscape === already) return prev;
                          const next = new Set(prev);
                          if (isActuallyLandscape) next.add(item.id);
                          else next.delete(item.id);
                          return next;
                        });
                      }}
                    />
                    {vm.layout_type === "top10" && (
                      <span className={styles.rankBadge}>{rank}</span>
                    )}
                    {vm.layout_type === "live-channels" && (
                      <span className={styles.liveBadge}>
                        <span className={styles.liveBadgeDot} />
                        LIVE
                      </span>
                    )}
                    {cardDesign.titlePosition === "overlay" && (
                      <div
                        className={styles.railOverlayScrim}
                        style={{
                          borderBottomLeftRadius: cardRadius,
                          borderBottomRightRadius: cardRadius,
                        }}
                      >
                        <span className={styles.railTitleOverlay}>{item.title || item.name}</span>
                      </div>
                    )}
                  </div>
                  {cardDesign.titlePosition === "below" && (
                    <span className={styles.railTitle}>{item.title || item.name}</span>
                  )}
                  {cardDesign.showSubtitle && cardDesign.titlePosition === "below" && item.content_type && (
                    <span className={styles.railSubtitle}>{item.content_type}</span>
                  )}
                </div>
              );
            });

            return cards;
          }}
        />
      </>
    );
  };

  // Shared between the inline canvas and the full-screen Preview modal so the
  // two never drift out of sync. Stacks every category of the active page,
  // like the real site would render it, instead of only the selected one.
  // `isModal`: the full-screen Preview is a different width, so only the
  // inline canvas reports rail overflow to the Section Settings panel.
  const renderCanvasFrame = (frameClassName, { isModal = false } = {}) => (
    <div
      className={`${styles.canvasFrame} ${frameClassName || ""} ${isDragOverCanvas ? styles.canvasFrameDragOver : ""}`}
      style={{ maxWidth: devicePreset.frameWidth || undefined, ...themeVars }}
      onDragOver={(e) => {
        if (leftTab !== "widgets") return;
        e.preventDefault();
        setIsDragOverCanvas(true);
      }}
      onDragLeave={() => setIsDragOverCanvas(false)}
      onDrop={(e) => {
        e.preventDefault();
        setIsDragOverCanvas(false);
        const value = e.dataTransfer.getData("text/plain");
        const droppedWidget = widgets.find((w) => w.value === value);
        if (droppedWidget) {
          createCategory(value, droppedWidget.label);
        }
      }}
    >
      {device === "web" && (
        <div className={styles.browserChromeBar}>
          <div className={styles.browserDots}>
            <span className={`${styles.browserDot} ${styles.browserDotRed}`} />
            <span className={`${styles.browserDot} ${styles.browserDotYellow}`} />
            <span className={`${styles.browserDot} ${styles.browserDotGreen}`} />
          </div>
          <div className={styles.browserChromeAddress}>{browserDomain}</div>
          <div className={styles.browserChromeSpacer} />
        </div>
      )}
      <div className={styles.canvasNav}>
        <span className={styles.canvasBrand}>
          {process.env.REACT_APP_TITLE || "Preview"}
        </span>
        {devicePreset.navStyle !== "bottom" && (
          <div className={styles.canvasNavLinks}>
            {sections
              .filter((sec) => sec.is_active)
              .map((sec) => (
                <button
                  key={sec.id}
                  className={`${styles.canvasNavLink} ${activeSectionId === sec.id ? styles.canvasNavLinkActive : ""}`}
                  onClick={() => handleSelectPage(sec.id)}
                >
                  {sec.name}
                </button>
              ))}
          </div>
        )}
        <div className={styles.canvasNavActions}>
          <span className={styles.canvasNavIconBtn} title="Search">
            <IoSearchOutline />
          </span>
          {devicePreset.navStyle === "bottom" && (
            <span className={styles.canvasNavIconBtn} title="Settings">
              <IoSettingsOutline />
            </span>
          )}
        </div>
      </div>
      <div
        className={styles.canvasBody}
        onClick={(e) => {
          // Only the empty background, not a bubbled click from a section
          // block, should deselect.
          if (e.target === e.currentTarget) handleCanvasBackgroundClick();
        }}
        onContextMenu={(e) => {
          // Blank canvas space (and the empty-page state) has nothing to
          // act on -- suppress the browser's native menu instead of leaving
          // it up. A right-click on a section block already preventDefault's
          // and opens its own menu before this bubbles up here.
          e.preventDefault();
        }}
      >
        {categories.length === 0 ? (
          <div className={styles.pageEmptyState}>
            <button
              className={styles.pageEmptyAddBtn}
              onClick={() => setLeftTab("widgets")}
              title="Open Widgets panel"
            >
              <IoAddOutline />
            </button>
            <h3 className={styles.pageEmptyTitle}>This page is empty</h3>
            <p className={styles.pageEmptyText}>
              Start from a template, or open the <strong>Widgets</strong> panel
              and drag in sections one by one.
            </p>
            <button className={styles.pageEmptyBrowseBtn} onClick={() => setTemplatesOpen(true)}>
              Browse templates
            </button>
          </div>
        ) : (
          categories.map((cat) => {
            const isSelectedCat = selected?.id
              ? cat.id === selected.id
              : Boolean(cat.draftKey) && cat.draftKey === selected?.draftKey;
            const vm = isSelectedCat
              ? postFormData
              : {
                name: cat.name,
                layout_type: cat.layout_type,
                videos: cat.contents_data || [],
                config: withConfigDefaults(cat.config, cat.layout_type),
              };
            return (
              <div
                key={cat.id || cat.draftKey || "draft"}
                ref={(el) => {
                  categoryBlockRefs.current[cat.id] = el;
                }}
                className={`${styles.canvasCategoryBlock} ${isSelectedCat ? styles.canvasCategoryBlockActive : ""}`}
                onClick={() => {
                  if (!isSelectedCat) handleSelectCategory(cat);
                }}
                onContextMenu={(e) => {
                  e.preventDefault();
                  setLayerMenu({ cat, x: e.clientX, y: e.clientY });
                }}
              >
                {isSelectedCat && (
                  <span className={styles.canvasSelectedBadge}>
                    {vm.name || "Untitled"}
                  </span>
                )}
                {renderCategoryPreview(vm, {
                  isSelected: isSelectedCat,
                  categoryId: cat.id,
                  reportOverflow: isSelectedCat && !isModal,
                })}
              </div>
            );
          })
        )}
      </div>
      {devicePreset.navStyle === "bottom" && (
        <div className={styles.canvasBottomNav}>
          {MOBILE_PREVIEW_NAV_ITEMS.map((item) => (
            <span
              key={item.key}
              className={`${styles.canvasBottomNavItem} ${item.key === "home" ? styles.canvasBottomNavItemActive : ""}`}
            >
              <span className={styles.canvasBottomNavIcon}>{item.icon}</span>
              <span className={styles.canvasBottomNavLabel}>{item.label}</span>
            </span>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <>
      <div className={styles.container}>
        {/* TOP BAR */}
        <div className={styles.topBar}>
          <button className={styles.dashboardBtn} onClick={handleBack}>
            <IoArrowBackOutline /> <span className={styles.btnLabel}>Go Back</span>
          </button>

          <div className={styles.brandBlock}>
            <div className={styles.brandBadge}>
              <img
                src={`${process.env.PUBLIC_URL}/${process.env.REACT_APP_LOGO}`}
                alt=""
                className={styles.brandLogo}
                onError={(e) => {
                  e.target.style.display = "none";
                }}
              />
              <span className={styles.brandInitial}>
                {(process.env.REACT_APP_TITLE || "E").charAt(0).toUpperCase()}
              </span>
            </div>
            <div className={styles.brandText}>
              {process.env.REACT_APP_TITLE && (
                <span className={styles.brandName}>{process.env.REACT_APP_TITLE}</span>
              )}
              <span className={styles.brandSub}>Experience Builder</span>
            </div>
          </div>

          <div className={styles.pageTabsInline}>
            {sections
              .filter((sec) => sec.is_active || sec.id === activeSectionId)
              .map((sec) => (
              <button
                key={sec.id}
                className={`${styles.pageTabInline} ${activeSectionId === sec.id ? styles.pageTabInlineActive : ""}`}
                onClick={() => handleSelectPage(sec.id)}
              >
                {sec.name}
              </button>
            ))}
            {canCreate && (
              <button
                className={styles.addPageTabBtn}
                onClick={handleQuickAddPage}
                title="Add new page"
                aria-label="Add new page"
              >
                <IoAddOutline />
              </button>
            )}
          </div>

          {(canCreate || canEdit || canDelete) && (
            <div className={styles.historyGroup}>
              <button
                className={styles.historyBtn}
                onClick={runUndo}
                disabled={undoStack.length === 0}
                title="Undo"
                aria-label="Undo"
              >
                <IoArrowUndoOutline />
              </button>
              <button
                className={styles.historyBtn}
                onClick={runRedo}
                disabled={redoStack.length === 0}
                title="Redo"
                aria-label="Redo"
              >
                <IoArrowRedoOutline />
              </button>
              <button
                className={styles.historyBtn}
                onClick={openVersionHistory}
                disabled={!currentSection}
                title="Version history"
                aria-label="Version history"
              >
                <IoHourglassOutline />
              </button>
            </div>
          )}

          <div className={styles.topBarDivider} />

          <div className={styles.topBarActions}>
            {currentSection && (
              <span className={`${styles.statusPill} ${currentSection.is_active ? styles.statusPillLive : styles.statusPillDraft}`}>
                <span className={styles.statusDot} />
                {currentSection.is_active ? "Live" : "Draft"}
              </span>
            )}
            <button className={styles.previewBtn} onClick={() => setPreviewOpen(true)} disabled={!selected}>
              <IoEyeOutline /> <span className={styles.btnLabel}>Preview</span>
            </button>
            <button className={styles.publishBtn} onClick={openPublishModal} disabled={!activeSectionId}>
              Publish
            </button>
          </div>
        </div>

        <div
          className={`${styles.grid} ${leftCollapsed ? styles.gridLeftCollapsed : ""} ${rightCollapsed ? styles.gridRightCollapsed : ""}`}
        >
          {/* RAIL STRIP */}
          {!leftCollapsed && (
            <div className={styles.railStrip}>
              {RAIL_TABS.map((tab) => (
                <button
                  key={tab.key}
                  className={`${styles.railTabBtn} ${leftTab === tab.key ? styles.railTabBtnActive : ""}`}
                  onClick={() => setLeftTab(tab.key)}
                >
                  {tab.icon}
                  <span>{tab.label}</span>
                </button>
              ))}
            </div>
          )}

          {/* LEFT PANEL */}
          {!leftCollapsed && (
            <div className={styles.sidebar} ref={sidebarRef} onDragOver={handleSidebarDragOver}>
              {leftTab === "widgets" && (
                <>
                  <div className={styles.widgetsStickyHeader}>
                    <div className={styles.widgetsHeader}>
                      <div>
                        <h2>Widgets Library</h2>
                        <p className={styles.widgetsHint}>Drag or click to add a section</p>
                      </div>
                    </div>
                  </div>
                  {!widgetGroups.length && (
                    <p className={styles.layersDragHint}>Loading widgets…</p>
                  )}
                  {widgetGroups.map((group) => (
                    <div key={group.heading} className={styles.widgetGroup}>
                      <p className={styles.widgetGroupHeading}>{group.heading}</p>
                      <div className={styles.widgetList}>
                        {group.items.map((w) => (
                          <div
                            key={w.value}
                            className={styles.widgetItem}
                            role="button"
                            tabIndex={0}
                            onClick={() => createCategory(w.value, w.label)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === " ") createCategory(w.value, w.label);
                            }}
                            draggable
                            onDragStart={(e) => {
                              e.dataTransfer.setData("text/plain", w.value);
                              e.dataTransfer.effectAllowed = "copy";
                            }}
                          >
                            <span className={styles.widgetIcon}>{w.icon}</span>
                            <span className={styles.widgetText}>
                              <span className={styles.widgetLabel}>{w.label}</span>
                              <span className={styles.widgetDesc}>{w.description}</span>
                            </span>
                            <span className={styles.widgetAddIcon}>
                              <IoAddOutline />
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </>
              )}

              {leftTab === "layers" && (
                <>
                  <div className={styles.widgetsStickyHeader}>
                    <div className={styles.widgetsHeader}>
                      <div>
                        <h2>Layers</h2>
                        <p className={styles.widgetsHint}>Reorder &amp; select page sections</p>
                      </div>
                    </div>
                    <p className={styles.layersDragHint}>Drag to reorder</p>
                  </div>
                  {categoryListMarkup}
                </>
              )}

              {leftTab === "theme" && theme && (
                <>
                <div className={styles.widgetsStickyHeader}>
                  <div className={styles.widgetsHeader}>
                    <div>
                      <h2>Theme Builder</h2>
                      <p className={styles.widgetsHint}>
                        Global look &amp; feel. Changes apply live to the preview.
                      </p>
                    </div>
                  </div>
                  </div>
                  <div className={styles.themeSection}>
                    <p className={styles.label}>Accent color</p>
                    <div className={styles.swatchRow}>
                      {THEME_ACCENT_PRESETS.map((color) => (
                        <button
                          key={color}
                          className={`${styles.swatch} ${isSameColor(theme.accent_color, color) ? styles.swatchActive : ""}`}
                          style={{ backgroundColor: color }}
                          onClick={() => patchTheme({ accent_color: color })}
                          aria-label={color}
                          title={color}
                        />
                      ))}
                      {(theme.custom_accent_colors || []).map((color) => (
                        <button
                          key={color}
                          className={`${styles.swatch} ${styles.swatchCustom} ${isSameColor(theme.accent_color, color) ? styles.swatchActive : ""}`}
                          style={{ backgroundColor: color }}
                          onClick={() => patchTheme({ accent_color: color })}
                          aria-label={color}
                          title={color}
                        >
                          {canEditTheme && (
                            <span
                              className={styles.swatchRemove}
                              role="button"
                              tabIndex={0}
                              title="Remove color"
                              onClick={(e) => removeAccentColor(color, e)}
                            >
                              <IoClose />
                            </span>
                          )}
                        </button>
                      ))}
                      {canEditTheme && (
                        <div className={styles.accentPickerWrap} ref={accentPickerRef}>
                          <button
                            className={`${styles.swatch} ${styles.swatchAdd}`}
                            onClick={() => {
                              setCustomColorError("");
                              setAccentPickerOpen((open) => !open);
                            }}
                            aria-label="Add custom color"
                            title="Add custom color"
                          >
                            <IoAddOutline />
                          </button>
                          <AnchoredPopover
                            anchorRef={accentPickerRef}
                            popoverRef={accentPopoverRef}
                            open={accentPickerOpen}
                            className={styles.accentPickerPopover}
                          >
                            <p className={styles.accentPickerLabel}>Choose a color</p>
                            <div className={styles.accentPaletteGrid}>
                              {THEME_COLOR_PALETTE.map((color) => (
                                <button
                                  key={color}
                                  className={styles.paletteSwatch}
                                  style={{ backgroundColor: color }}
                                  onClick={() => addAccentColor(color)}
                                  aria-label={color}
                                  title={color}
                                />
                              ))}
                            </div>
                            <div className={styles.accentCustomRow}>
                              <input
                                type="color"
                                className={styles.accentColorInput}
                                value={HEX_COLOR_RE.test(customColorInput) ? customColorInput : "#3E7BFA"}
                                onChange={(e) => setCustomColorInput(e.target.value)}
                              />
                              <input
                                type="text"
                                className={styles.input}
                                value={customColorInput}
                                onChange={(e) => setCustomColorInput(e.target.value)}
                                placeholder="#3E7BFA"
                              />
                              <button
                                className={styles.accentCustomAddBtn}
                                onClick={() => addAccentColor(customColorInput)}
                              >
                                Add
                              </button>
                            </div>
                            {customColorError && <p className={styles.accentPickerError}>{customColorError}</p>}
                          </AnchoredPopover>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className={styles.themeSection}>
                    <p className={styles.label}>App background</p>
                    <div className={styles.backgroundGrid}>
                      {THEME_BACKGROUND_PRESETS.map((bg) => (
                        <button
                          key={bg.value}
                          className={`${styles.backgroundOption} ${isSameColor(theme.app_background, bg.color) ? styles.backgroundOptionActive : ""}`}
                          onClick={() => patchTheme({ app_background: bg.color })}
                        >
                          <span className={styles.backgroundSwatch} style={{ backgroundColor: bg.color }} />
                          {bg.label}
                        </button>
                      ))}
                      {(theme.custom_backgrounds || []).map((color) => (
                        <button
                          key={color}
                          className={`${styles.backgroundOption} ${styles.swatchCustom} ${isSameColor(theme.app_background, color) ? styles.backgroundOptionActive : ""}`}
                          onClick={() => patchTheme({ app_background: color })}
                        >
                          <span className={styles.backgroundSwatch} style={{ backgroundColor: color }} />
                          {color}
                          {canEditTheme && (
                            <span
                              className={styles.swatchRemove}
                              role="button"
                              tabIndex={0}
                              title="Remove background"
                              onClick={(e) => removeBackgroundColor(color, e)}
                            >
                              <IoClose />
                            </span>
                          )}
                        </button>
                      ))}
                      {canEditTheme && (
                        <div className={styles.accentPickerWrap} ref={bgPickerRef}>
                          <button
                            className={`${styles.backgroundOption} ${styles.swatchAdd}`}
                            onClick={() => {
                              setCustomBgError("");
                              setBgPickerOpen((open) => !open);
                            }}
                          >
                            <IoAddOutline />
                            Custom
                          </button>
                          <AnchoredPopover
                            anchorRef={bgPickerRef}
                            popoverRef={bgPopoverRef}
                            open={bgPickerOpen}
                            className={styles.accentPickerPopover}
                          >
                            <p className={styles.accentPickerLabel}>Choose a background color</p>
                            <div className={styles.accentPaletteGrid}>
                              {THEME_COLOR_PALETTE.map((color) => (
                                <button
                                  key={color}
                                  className={styles.paletteSwatch}
                                  style={{ backgroundColor: color }}
                                  onClick={() => addBackgroundColor(color)}
                                  aria-label={color}
                                  title={color}
                                />
                              ))}
                            </div>
                            <div className={styles.accentCustomRow}>
                              <input
                                type="color"
                                className={styles.accentColorInput}
                                value={HEX_COLOR_RE.test(customBgInput) ? customBgInput : "#1A1A1A"}
                                onChange={(e) => setCustomBgInput(e.target.value)}
                              />
                              <input
                                type="text"
                                className={styles.input}
                                value={customBgInput}
                                onChange={(e) => setCustomBgInput(e.target.value)}
                                placeholder="#1A1A1A"
                              />
                              <button
                                className={styles.accentCustomAddBtn}
                                onClick={() => addBackgroundColor(customBgInput)}
                              >
                                Add
                              </button>
                            </div>
                            {customBgError && <p className={styles.accentPickerError}>{customBgError}</p>}
                          </AnchoredPopover>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className={styles.themeSection}>
                    <p className={styles.label}>Font color</p>
                    <div className={styles.swatchRow}>
                      {THEME_FONT_COLOR_PRESETS.map((color) => (
                        <button
                          key={color}
                          className={`${styles.swatch} ${isSameColor(fontColor, color) ? styles.swatchActive : ""}`}
                          style={{ backgroundColor: color }}
                          onClick={() => selectFontColor(color)}
                          aria-label={color}
                          title={color}
                        />
                      ))}
                      {(theme.custom_font_colors || []).map((color) => (
                        <button
                          key={color}
                          className={`${styles.swatch} ${styles.swatchCustom} ${isSameColor(fontColor, color) ? styles.swatchActive : ""}`}
                          style={{ backgroundColor: color }}
                          onClick={() => selectFontColor(color)}
                          aria-label={color}
                          title={color}
                        >
                          {canEditTheme && (
                            <span
                              className={styles.swatchRemove}
                              role="button"
                              tabIndex={0}
                              title="Remove font color"
                              onClick={(e) => removeFontColor(color, e)}
                            >
                              <IoClose />
                            </span>
                          )}
                        </button>
                      ))}
                      {canEditTheme && (
                        <div className={styles.accentPickerWrap} ref={fontPickerRef}>
                          <button
                            className={`${styles.swatch} ${styles.swatchAdd}`}
                            onClick={() => {
                              setCustomFontError("");
                              setFontPickerOpen((open) => !open);
                            }}
                            aria-label="Add custom font color"
                            title="Add custom font color"
                          >
                            <IoAddOutline />
                          </button>
                          <AnchoredPopover
                            anchorRef={fontPickerRef}
                            popoverRef={fontPopoverRef}
                            open={fontPickerOpen}
                            className={styles.accentPickerPopover}
                          >
                            <p className={styles.accentPickerLabel}>Choose a font color</p>
                            <div className={styles.accentPaletteGrid}>
                              {THEME_COLOR_PALETTE.map((color) => (
                                <button
                                  key={color}
                                  className={styles.paletteSwatch}
                                  style={{ backgroundColor: color }}
                                  onClick={() => addFontColor(color)}
                                  aria-label={color}
                                  title={color}
                                />
                              ))}
                            </div>
                            <div className={styles.accentCustomRow}>
                              <input
                                type="color"
                                className={styles.accentColorInput}
                                value={HEX_COLOR_RE.test(customFontInput) ? customFontInput : "#FFFFFF"}
                                onChange={(e) => setCustomFontInput(e.target.value)}
                              />
                              <input
                                type="text"
                                className={styles.input}
                                value={customFontInput}
                                onChange={(e) => setCustomFontInput(e.target.value)}
                                placeholder="#FFFFFF"
                              />
                              <button
                                className={styles.accentCustomAddBtn}
                                onClick={() => addFontColor(customFontInput)}
                              >
                                Add
                              </button>
                            </div>
                            {customFontError && <p className={styles.accentPickerError}>{customFontError}</p>}
                          </AnchoredPopover>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className={styles.themeSection}>
                    <p className={styles.label}>Corner radius · {theme.corner_radius}px</p>
                    <input
                      type="range"
                      min="0"
                      max="24"
                      value={theme.corner_radius}
                      onChange={(e) => patchTheme({ corner_radius: parseInt(e.target.value, 10) })}
                      className={styles.rangeInput}
                    />
                  </div>

                  <div className={styles.themeSection}>
                    <p className={styles.label}>Focus effect · TV / remote</p>
                    <div className={styles.segmentedRow}>
                      {THEME_FOCUS_EFFECTS.map((effect) => (
                        <button
                          key={effect.value}
                          className={`${styles.segmentedBtn} ${theme.focus_effect === effect.value ? styles.segmentedBtnActive : ""}`}
                          onClick={() => patchTheme({ focus_effect: effect.value })}
                        >
                          {effect.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className={styles.themeSection}>
                    <p className={styles.label}>Typography</p>
                    <div className={styles.fontDisplayCard}>
                      <span className={styles.fontDisplayLabel}>Heading / Body font</span>
                      <p className={styles.fontDisplayName}>{theme.font_family}</p>
                      <p className={styles.fontDisplaySample} style={{ fontFamily: theme.font_family }}>
                        The quick brown fox
                      </p>
                    </div>
                  </div>
                </>
              )}

              {leftTab === "pages" && (
                <>
                  <div className={styles.pagesStickyHeader}>
                    <div className={styles.widgetsHeader}>
                      <div>
                        <h2>Pages</h2>
                        <p className={styles.widgetsHint}>Reorder &amp; select page sections</p>
                      </div>
                    </div>

                    <div className={styles.pagesActionsRow}>
                      <button
                        className={styles.newPageBtn}
                        onClick={openAddPage}
                      >
                        <IoAddOutline />
                        New Page
                      </button>

                      <button
                        className={styles.templatesBtn}
                        onClick={() => setTemplatesOpen(true)}
                      >
                        Templates
                      </button>
                    </div>
                  </div>

                  <div className={styles.pageCardList}>
                    {orderedSections.map((sec) => {
                      const isLong = sec.name.length > 7;

                      return (
                        <div
                          key={sec.id}
                          className={`${styles.pageCard} ${activeSectionId === sec.id
                            ? styles.pageCardActive
                            : ""
                            }`}
                          onClick={() => handleSelectPage(sec.id)}
                          onContextMenu={(e) => {
                            e.preventDefault();
                            setPageMenu({ sec, x: e.clientX, y: e.clientY });
                          }}
                          draggable={canEdit && editingPageName?.id !== sec.id}
                          onDragStart={(e) => {
                            dragPageIdRef.current = sec.id;
                            e.dataTransfer.effectAllowed = "move";
                          }}
                          onDragEnter={() => handlePageDragEnter(sec.id)}
                          onDragOver={(e) => e.preventDefault()}
                          onDragEnd={handlePageDragEnd}
                        >
                          <div className={styles.pageCardIcon}>
                            <IoNewspaperOutline />
                          </div>

                          <div className={styles.pageCardBody}>
                            <div className={styles.pageCardTitleRow}>
                              {editingPageName?.id === sec.id ? (
                                <input
                                  ref={editingPageNameRef}
                                  className={styles.pageCardNameInput}
                                  value={editingPageName.value}
                                  onClick={(e) => e.stopPropagation()}
                                  onChange={(e) =>
                                    setEditingPageName((prev) => ({ ...prev, value: e.target.value }))
                                  }
                                  onBlur={commitRenamePage}
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                      e.currentTarget.blur();
                                    } else if (e.key === "Escape") {
                                      setEditingPageName(null);
                                    }
                                  }}
                                />
                              ) : (
                                <span
                                  className={`${styles.pageCardName} ${isLong
                                    ? styles.pageCardNameScrollable
                                    : ""
                                    }`}
                                  onDoubleClick={(e) => {
                                    e.stopPropagation();
                                    startRenamePage(sec);
                                  }}
                                >
                                  <span className={styles.pageCardNameStatic}>
                                    {sec.name}
                                  </span>

                                  <span className={styles.pageCardNameInner}>
                                    <span className={styles.pageCardNameText}>
                                      {sec.name}
                                    </span>

                                    <span
                                      className={styles.pageCardNameText}
                                      aria-hidden="true"
                                    >
                                      {sec.name}
                                    </span>
                                  </span>
                                </span>
                              )}

                              {sec.is_home && (
                                <span className={styles.pageCardBadge}>
                                  HOME
                                </span>
                              )}
                            </div>

                            <span className={styles.pageCardMeta}>
                              {sec.categories?.length || 0} sections
                              {!sec.is_active ? " · hidden" : ""}
                            </span>
                          </div>
                          {!sec.is_active && (
                            <div className={styles.pageRowActions}>
                              <span className={styles.hiddenPageIcon} title="Hidden from menu">
                                <IoRemoveCircleOutline />
                              </span>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  <div className={styles.pageInfoBox}>
                    <p className={styles.pageInfoTitle}>
                      WHAT'S A PAGE?
                    </p>

                    <p className={styles.pageInfoText}>
                      Each page is a full screen in your app (Home, Movies,
                      Kids…). Open one to build its sections, hide it from
                      menus, or set it as the launch screen.
                    </p>
                  </div>
                </>
              )}
            </div>
          )}

          {/* CANVAS PREVIEW */}
          <div className={styles.canvas}>
            <div className={`${styles.canvasToolbar} ${leftCollapsed && rightCollapsed ? styles.canvasToolbarFull : ""}`}>
              <button
                className={styles.collapseBtn}
                onClick={() => setLeftCollapsed((v) => !v)}
                title={leftCollapsed ? "Show panel" : "Hide panel"}
              >
                <IoArrowBackOutline className={leftCollapsed ? styles.flip : ""} />
              </button>

              <div className={styles.deviceToggle}>
                {Object.entries(DEVICE_PRESETS).map(([key, preset]) => (
                  <button
                    key={key}
                    className={`${styles.deviceToggleBtn} ${device === key ? styles.deviceToggleBtnActive : ""}`}
                    onClick={() => setDevice(key)}
                  >
                    {preset.label}
                  </button>
                ))}
              </div>

              <span className={styles.canvasInfo}>
                {categories.length} sections · {currentSection?.name || "No page"} page
              </span>

              <div className={styles.toolbarRightGroup}>
                {selected && (
                  <span className={styles.saveStatusPill}>
                    <span className={styles.statusDot} />
                    {dirty ? "Saving draft…" : "Draft saved · Publish to go live"}
                  </span>
                )}
                <button
                  className={styles.collapseBtn}
                  onClick={() => setRightCollapsed((v) => !v)}
                  title={rightCollapsed ? "Show settings" : "Hide settings"}
                >
                  <IoArrowForwardOutline className={rightCollapsed ? styles.flip : ""} />
                </button>
              </div>
            </div>

            {renderCanvasFrame(
              `${styles[`canvasFrame_${device}`]} ${leftCollapsed && rightCollapsed ? styles.canvasFrameFull : ""}`
            )}
            <p className={styles.canvasMeta}>
              {devicePreset.caption} · {postFormData.videos?.length || 0} items ·{" "}
              {postFormData.layout_type || "—"}
            </p>
          </div>

          {/* RIGHT PANEL */}
          {!rightCollapsed && (
            <div className={styles.editor}>
              {selected ? (
                <>
                  <div className={styles.SectionStickyHeader}>
                    <div className={styles.editorHeader}>
                      <h2>Section Settings</h2>
                      <div className={styles.editorHeaderActions}>
                        {canEdit && (
                          <>
                            <button
                              className={styles.editorHeaderIconBtn}
                              onClick={() => moveCategory("up")}
                              disabled={selectedCategoryIndex <= 0}
                              title="Move up"
                            >
                              <IoArrowUpOutline />
                            </button>
                            <button
                              className={styles.editorHeaderIconBtn}
                              onClick={() => moveCategory("down")}
                              disabled={selectedCategoryIndex === -1 || selectedCategoryIndex >= categories.length - 1}
                              title="Move down"
                            >
                              <IoArrowDownOutline />
                            </button>
                          </>
                        )}
                        {canCreate && !isNew && (
                          <button className={styles.editorHeaderIconBtn} onClick={() => duplicateCategory()} title="Duplicate">
                            <IoCopyOutline />
                          </button>
                        )}
                        {canDelete && (
                          <button className={styles.editorHeaderDeleteBtn} onClick={() => handleDeleteCategory(selected)} title="Delete">
                            <IoTrashOutline />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className={styles.inputBoxWrapper}>
                    <div className={styles.inputBox}>
                      <label className={styles.settingsGroupHeading}>Category Name</label>
                      <input
                        className={styles.input}
                        type="text"
                        placeholder="New Category"
                        value={postFormData.name}
                        disabled={!canEdit && !isNew}
                        onChange={(e) => {
                          setPostFormData((prevState) => ({
                            ...prevState,
                            name: e.target.value,
                          }));
                          setDirty(true);
                        }}
                      />
                    </div>

                    <div className={styles.inputBox}>
                      <label className={styles.settingsGroupHeading}>Layout Type</label>
                      <select
                        className={styles.input}
                        value={postFormData.layout_type}
                        disabled={!canEdit && !isNew}
                        onChange={(e) => {
                          const nextLayoutType = e.target.value;
                          setPostFormData((prevState) => ({
                            ...prevState,
                            layout_type: nextLayoutType,
                            config: FIXED_RATIO_LAYOUTS[nextLayoutType]
                              ? {
                                  ...prevState.config,
                                  cardDesign: {
                                    ...prevState.config?.cardDesign,
                                    imageRatio: FIXED_RATIO_LAYOUTS[nextLayoutType].ratio,
                                  },
                                }
                              : prevState.config,
                          }));
                          setDirty(true);
                        }}
                      >
                        {widgets?.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                        {postFormData.layout_type &&
                          !widgets?.some((w) => w.value === postFormData.layout_type) && (
                            // The widget this category was saved with no longer
                            // exists in the library -- without this, the select
                            // silently falls back to displaying whichever option
                            // happens to be first, misrepresenting what's saved.
                            <option value={postFormData.layout_type}>
                              {postFormData.layout_type} (widget removed)
                            </option>
                          )}
                      </select>
                    </div>

                    <div className={styles.inputBox}>
                      <label className={styles.settingsGroupHeading}>Data Source</label>
                      <select
                        className={styles.input}
                        value={postFormData.config?.dataSource || "manual"}
                        disabled={!canEdit && !isNew}
                        onChange={(e) => setDataSource(e.target.value)}
                      >
                        {DATA_SOURCE_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    </div>

                    {/* Genre picker is available for every data source, so a
                        section can pull content in by genre alongside (or
                        instead of) hand-picked Media Library titles. */}
                    <div className={styles.inputBox}>
                      <label className={styles.settingsGroupHeading}>Genre</label>
                      <div className={styles.tagRow}>
                        <select
                          value={tagInput}
                          disabled={!canEdit && !isNew}
                          onChange={(e) => {
                            const value = e.target.value;
                            setTagInput(value);
                            handleTagsAdd(value);
                          }}
                          className={styles.input}
                        >
                          <option value="" disabled>
                            Select genre
                          </option>
                          {genres
                            ?.filter((g) => !postFormData?.genres?.some((sel) => sel?.id === g.id))
                            .map((tag) => (
                              <option key={tag.id} value={tag.id}>
                                {tag.name}
                              </option>
                            ))}
                        </select>
                      </div>
                      {postFormData?.genres?.length > 0 && (
                        <div className={styles.chipBox}>
                          {postFormData?.genres?.map((id, index) => {
                            return (
                              <div key={index} className={styles.chipWrapper}>
                                <Chips
                                  value={id?.name || `ID ${id}`}
                                  type="selected"
                                />
                                <span
                                  className={styles.removeTag}
                                  onClick={() => handleRemoveTag(id)}
                                >
                                  ×
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>

                    {isAlgorithmic && (
                      <p className={styles.algorithmicNote}>
                        Content is selected automatically for this data source.
                        {postFormData.config?.dataSource === "continue-watching" &&
                          (postFormData.videos?.length
                            ? " The canvas preview shows sample data — real users see their own history."
                            : " No preview available until this section is saved.")}
                        {" "}You can still add specific titles below to feature alongside it.
                      </p>
                    )}

                    {isPlaceholderSource && (
                      <p className={styles.algorithmicNote}>
                        This data source isn't wired up yet — selecting it won't
                        pull content automatically. Add specific titles below via
                        Browse Media Library in the meantime.
                      </p>
                    )}

                    {/* Browse Media Library and the selected-content list stay
                        available for every data source -- even an
                        algorithmic rail (Trending, Continue Watching,
                        Recently Added) can be supplemented with hand-picked
                        backup content. */}
                    <div className={styles.inputBox}>
                      <button
                        className={styles.browseBtn}
                        onClick={() => setIsOpen(true)}
                      >
                        ＋ Browse Media Library
                      </button>
                    </div>

                    {isOpen && (
                      <MediaLibraryModal
                        isOpen={isOpen}
                        setIsOpen={setIsOpen}
                        mode="modal"
                        showCheckbox
                        selectMultipal={true}
                        onMultiVideoSelect={handleVideoSelection}
                        preSelected={postFormData.contents}
                        unCheckHandleValue={(item) => handleVideoUnCheck(item)}
                        sectionManager="sectionManager"
                      />
                    )}

                    {/* Selected Videos */}
                    <div
                      className={styles.emptyBox}
                      ref={scrollRef}
                      onDragOver={!videoSearch ? handleDragOver : undefined}
                    >
                      <div className={styles.label}>
                        Selected Content ({postFormData.videos?.length || 0})
                      </div>

                      {/* Search Bar */}
                      <div className={styles.searchBarWrapper}>
                        <span className={styles.searchIcon}>
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z" />
                          </svg>
                        </span>
                        <input
                          type="text"
                          className={styles.searchInput}
                          placeholder="search by media name..."
                          value={videoSearch}
                          onChange={(e) => setVideoSearch(e.target.value)}
                        />
                      </div>

                      {postFormData?.videos?.length ? (
                        (() => {
                          const filtered = postFormData.videos.filter((item) =>
                            (item.title || item.name || "")
                              .toLowerCase()
                              .includes(videoSearch.toLowerCase())
                          );
                          return filtered.length ? (
                            filtered.map((item, index) => (
                              <div
                                key={item.id}
                                className={styles.card}
                                draggable={!videoSearch}
                                onDragStart={!videoSearch ? (e) => handleDragStart(e, item, index) : undefined}
                                onDrop={!videoSearch ? (e) => handleDrop(e, index) : undefined}
                                onDragEnd={!videoSearch ? handleDragEnd : undefined}
                              >
                                <span className={styles.dragIcon}>⋮⋮</span>
                                <img
                                  src={item.thumbnail || item?.portrait}
                                  className={styles.thumbnail}
                                  alt=""
                                  onError={(e) => {
                                    e.currentTarget.onerror = null;
                                    e.currentTarget.src =
                                      "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBTAA7";
                                  }}
                                />
                                <div className={styles.contentInfo}>
                                  <span
                                    className={styles.title}
                                    title={item.title || item.name}
                                  >
                                    {item.title || item.name}
                                  </span>
                                  <span className={styles.type}>{item.type}</span>
                                </div>
                                <span className={styles.position}>#{index + 1}</span>
                                <div className={styles.actionButtons}>
                                  <button onClick={() => handleDelete(item.id)}>
                                    <MdDelete />
                                  </button>
                                </div>
                              </div>
                            ))
                          ) : (
                            <div className={styles.emptyBox1}>No content found</div>
                          );
                        })()
                      ) : (
                        <div className={styles.emptyBox1}>
                          No content added yet <br />
                          <span>Use tags to find and add content</span>
                        </div>
                      )}
                    </div>

                    <div className={styles.settingsGroupHeading}>CARD DESIGN</div>
                    <div className={styles.cardDesignRow}>
                      <div className={styles.inputBox}>
                        <label className={styles.label}>Image ratio</label>
                        <select
                          className={styles.input}
                          value={getEffectiveImageRatio(postFormData.layout_type, postFormData.config?.cardDesign)}
                          onChange={(e) => updateCardDesign("imageRatio", e.target.value)}
                          disabled={!!FIXED_RATIO_LAYOUTS[postFormData.layout_type]}
                          title={FIXED_RATIO_LAYOUTS[postFormData.layout_type]?.reason}
                        >
                          {IMAGE_RATIO_OPTIONS.map((opt) => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                          ))}
                        </select>
                      </div>
                      <div className={styles.inputBox}>
                        <label className={styles.label}>Title position</label>
                        <select
                          className={styles.input}
                          value={postFormData.config?.cardDesign?.titlePosition}
                          onChange={(e) => updateCardDesign("titlePosition", e.target.value)}
                        >
                          {TITLE_POSITION_OPTIONS.map((opt) => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div className={styles.toggleRow}>
                      <span className={styles.label}>Drop shadow</span>
                      <input
                        type="checkbox"
                        checked={!!postFormData.config?.cardDesign?.dropShadow}
                        onChange={(e) => updateCardDesign("dropShadow", e.target.checked)}
                      />
                    </div>

                    <div className={styles.toggleRow}>
                      <span className={styles.label}>Show subtitle / metadata</span>
                      <input
                        type="checkbox"
                        checked={!!postFormData.config?.cardDesign?.showSubtitle}
                        onChange={(e) => updateCardDesign("showSubtitle", e.target.checked)}
                      />
                    </div>

                    {postFormData.layout_type === "live-channels" && (
                      <div className={styles.toggleRow}>
                        <span className={styles.label}>LIVE badge</span>
                        <input
                          type="checkbox"
                          checked={!!postFormData.config?.cardDesign?.showLiveBadge}
                          onChange={(e) => updateCardDesign("showLiveBadge", e.target.checked)}
                        />
                      </div>
                    )}

                    <div className={styles.settingsGroupHeading}>CARDS VISIBLE PER DEVICE</div>
                    <div className={styles.cardsVisibleRow}>
                      {["web", "tv", "mobile"].map((deviceKey) => (
                        <div key={deviceKey} className={styles.inputBox}>
                          <label className={styles.label}>{DEVICE_PRESETS[deviceKey].label}</label>
                          <input
                            type="number"
                            min={CARDS_VISIBLE_MIN}
                            max={CARDS_VISIBLE_MAX}
                            className={styles.input}
                            value={postFormData.config?.cardsVisible?.[deviceKey] ?? ""}
                            onChange={(e) => updateCardsVisible(deviceKey, e.target.value)}
                            onBlur={() => commitCardsVisible(deviceKey)}
                          />
                        </div>
                      ))}
                    </div>

                    <div className={styles.settingsGroupHeading}>BEHAVIOUR</div>

                    {/* Each toggle is greyed out until the section has enough
                        items for it to do anything (see effectiveBehaviour). */}
                    <div
                      className={styles.toggleRow}
                      style={canAutoScroll ? undefined : { opacity: 0.45 }}
                      title={canAutoScroll ? undefined : autoScrollHint}
                    >
                      <span className={styles.label}>Auto scroll</span>
                      <input
                        type="checkbox"
                        checked={autoScrollOn}
                        disabled={!canAutoScroll}
                        onChange={(e) => updateBehaviour("autoScroll", e.target.checked)}
                        disabled={scrollBehaviourLocked}
                      />
                    </div>

                    {/* Infinite scroll only decides whether auto scroll loops,
                        so it's inert (and greyed out) while auto scroll is off. */}
                    <div
                      className={styles.toggleRow}
                      style={autoScrollOn ? undefined : { opacity: 0.45 }}
                      title={
                        !canAutoScroll
                          ? autoScrollHint
                          : !autoScrollOn
                            ? "Turn on Auto scroll first"
                            : undefined
                      }
                    >
                      <span className={styles.label}>Infinite scroll</span>
                      <input
                        type="checkbox"
                        checked={autoScrollOn && activeBehaviour.infiniteScroll}
                        disabled={!autoScrollOn}
                        onChange={(e) => updateBehaviour("infiniteScroll", e.target.checked)}
                        disabled={scrollBehaviourLocked}
                      />
                    </div>

                    <div
                      className={styles.toggleRow}
                      style={canShuffle ? undefined : { opacity: 0.45 }}
                      title={canShuffle ? undefined : `Add at least ${SHUFFLE_MIN_ITEMS} items to shuffle`}
                    >
                      <span className={styles.label}>Shuffle order</span>
                      <input
                        type="checkbox"
                        checked={activeBehaviour.shuffleOrder}
                        disabled={!canShuffle}
                        onChange={(e) => updateBehaviour("shuffleOrder", e.target.checked)}
                      />
                    </div>

                    <div className={styles.inputBox}>
                      <label className={styles.label}>Sorting</label>
                      {/* Shuffle overrides sorting in the preview, so lock the
                          dropdown while it's on instead of letting it silently do nothing. */}
                      <select
                        className={styles.input}
                        disabled={activeBehaviour.shuffleOrder}
                        title={activeBehaviour.shuffleOrder ? "Turn off Shuffle order to sort" : undefined}
                        value={postFormData.config?.behaviour?.sorting || "manual"}
                        onChange={(e) => updateBehaviour("sorting", e.target.value)}
                      >
                        {SORTING_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </>
              ) : (
                <div className={styles.noEditorState}>
                  <BsGrid3X3GapFill className={styles.noEditorIcon} />
                  <p className={styles.noEditorText}>
                    {activeSectionId
                      ? "Select a section on the canvas to edit its layout, data source, card design and behaviour."
                      : "Add a page to get started"}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <SectionModal
        isOpen={sectionModalOpen}
        onClose={() => setSectionModalOpen(false)}
        initialData={editingSection}
        onSaved={(page) => pushHistory({ type: "page-add", ...page })}
        fetchSection={async () => {
          if (dirty && selected) {
            const saved = await persistCategory();
            if (!saved) return;
          }
          const isNewPage = !editingSection;
          await fetchSectionsList({ selectNewest: true });
          if (isNewPage) {
            setLeftTab("widgets");
          }
        }}
      />


      {templatesOpen && (
        <TemplatePickerModal
          templates={PAGE_TEMPLATES}
          onSelect={handleSelectTemplate}
          onClose={() => setTemplatesOpen(false)}
        />
      )}

      {pendingDeletePage && (
        <ConfirmModal
          title="Delete this page?"
          subtitle="This removes the page and all its categories. If it's live, it stays on the site until you next publish. This can't be undone."
          onCancel={() => setPendingDeletePage(null)}
          onConfirm={confirmDeletePage}
          confirmLabel="Delete page"
          confirmClassName={confirmModalStyles.dangerBtn}
        />
      )}

      {layerMenu && (
        <ContextMenu
          // Forces a fresh mount (and a fresh position calculation) whenever
          // a different row is right-clicked -- reusing the same instance
          // when x/y happen to match a previous open (e.g. after scrolling)
          // would keep that previous menu's clamped position even though
          // this row's menu content may be a different height.
          key={layerMenu.cat.id || layerMenu.cat.draftKey || "draft"}
          x={layerMenu.x}
          y={layerMenu.y}
          popoverRef={layerMenuRef}
          onClose={() => setLayerMenu(null)}
        >
          {canCreate && layerMenu.cat.id && (
            <button
              className={styles.layerContextMenuItem}
              onClick={() => {
                duplicateCategory(layerMenu.cat);
                setLayerMenu(null);
              }}
            >
              <IoCopyOutline /> Duplicate
            </button>
          )}
          {canEdit && layerMenu.cat.id && (
            <button
              className={styles.layerContextMenuItem}
              onClick={() => {
                moveCategoryToTop(layerMenu.cat);
                setLayerMenu(null);
              }}
            >
              <TbArrowBarToUp /> Move to top
            </button>
          )}
          {canDelete && (
            <button
              className={`${styles.layerContextMenuItem} ${styles.layerContextMenuItemDanger}`}
              onClick={() => {
                handleDeleteCategory(layerMenu.cat);
                setLayerMenu(null);
              }}
            >
              <IoTrashOutline /> Delete
            </button>
          )}
        </ContextMenu>
      )}

      {pageMenu && (
        <ContextMenu
          key={pageMenu.sec.id}
          x={pageMenu.x}
          y={pageMenu.y}
          popoverRef={pageMenuRef}
          onClose={() => setPageMenu(null)}
        >
          {canEdit && (
            <button
              className={styles.layerContextMenuItem}
              onClick={() => {
                startRenamePage(pageMenu.sec);
                setPageMenu(null);
              }}
            >
              <IoPencilOutline /> Rename
            </button>
          )}
          {canEdit && (
            <button
              className={styles.layerContextMenuItem}
              onClick={() => {
                openEditPage(pageMenu.sec);
                setPageMenu(null);
              }}
            >
              <IoSettingsOutline /> Edit page
            </button>
          )}
          {canCreate && (
            <button
              className={styles.layerContextMenuItem}
              onClick={() => {
                handleDuplicatePage(pageMenu.sec);
                setPageMenu(null);
              }}
            >
              <IoCopyOutline /> Duplicate page
            </button>
          )}
          {canEdit && !pageMenu.sec.is_home && (
            <button
              className={styles.layerContextMenuItem}
              onClick={() => {
                handleSetHomePage(pageMenu.sec);
                setPageMenu(null);
              }}
            >
              <IoHomeOutline /> Set as home
            </button>
          )}
          {canEdit && (
            <button
              className={styles.layerContextMenuItem}
              onClick={() => {
                handleTogglePageVisibility(pageMenu.sec);
                setPageMenu(null);
              }}
            >
              {pageMenu.sec.is_active ? (
                <>
                  <IoEyeOffOutline /> Hide from menu
                </>
              ) : (
                <>
                  <IoEyeOutline /> Show in menu
                </>
              )}
            </button>
          )}
          {canDelete && (
            <button
              className={`${styles.layerContextMenuItem} ${styles.layerContextMenuItemDanger}`}
              onClick={() => {
                handleDeletePage(pageMenu.sec.id);
                setPageMenu(null);
              }}
            >
              <IoTrashOutline /> Delete page
            </button>
          )}
        </ContextMenu>
      )}

      {pendingDeleteCategory && (
        <ConfirmModal
          title="Delete this category?"
          subtitle="This can't be undone."
          onCancel={() => setPendingDeleteCategory(null)}
          onConfirm={confirmDeleteCategory}
          confirmLabel="Delete category"
          confirmClassName={confirmModalStyles.dangerBtn}
        />
      )}

      {pendingTemplate && (
        <ConfirmModal
          title="Replace all sections?"
          subtitle={`Applying "${pendingTemplate.label}" replaces every section on the current page. This can't be undone.`}
          onCancel={() => setPendingTemplate(null)}
          onConfirm={() => {
            const template = pendingTemplate;
            setPendingTemplate(null);
            applyTemplate(template);
          }}
          confirmLabel="Replace sections"
          confirmClassName={confirmModalStyles.dangerBtn}
        />
      )}

      {publishModalOpen && (
        <ConfirmModal
          title="Publish changes"
          subtitle={
            <>Push this draft live to your {process.env.REACT_APP_TITLE || "app"} apps.</>
          }
          onCancel={() => setPublishModalOpen(false)}
          onConfirm={confirmPublish}
          confirmLabel={scheduleEnabled ? "Schedule" : "Publish now"}
          confirmClassName={styles.publishBtn}
        >
          <p className={styles.publishSectionLabel}>Target platforms</p>
          <div className={styles.publishPlatforms}>
            {[
              { key: "web", label: "Web", icon: <IoDesktopOutline /> },
              { key: "tv", label: "TV", icon: <IoTvOutline /> },
              { key: "mobile", label: "Mobile", icon: <IoPhonePortraitOutline /> },
            ].map((platform) => (
              <label
                key={platform.key}
                className={`${styles.publishPlatform} ${publishTargets.includes(platform.key) ? styles.publishPlatformActive : ""}`}
              >
                <input
                  type="checkbox"
                  checked={publishTargets.includes(platform.key)}
                  onChange={() => togglePublishTarget(platform.key)}
                />
                {platform.icon} {platform.label}
              </label>
            ))}
          </div>

          <p className={styles.publishSectionLabel}>What changed</p>
          {publishChangesLoading ? (
            <p className={styles.publishSummary}>Checking for changes…</p>
          ) : publishChanges.length ? (
            <div className={styles.changeList}>
              {publishChanges.map((change) => (
                <div key={change.id} className={styles.changeRow}>
                  <span className={`${styles.changeIcon} ${styles[`changeIcon_${change.type}`] || ""}`}>
                    {change.type === "added" ? <IoAddCircleOutline />
                      : change.type === "removed" ? <IoTrashOutline />
                      : change.type === "theme" ? <IoColorPaletteOutline />
                      : <IoPencilOutline />}
                  </span>
                  {change.label}
                </div>
              ))}
            </div>
          ) : (
            <p className={styles.publishSummary}>No changes since the last publish.</p>
          )}

          <label className={styles.scheduleCheckboxRow}>
            <input
              type="checkbox"
              checked={scheduleEnabled}
              onChange={(e) => setScheduleEnabled(e.target.checked)}
            />
            Schedule for later instead of publishing now
          </label>
          {scheduleEnabled && (
            <ScheduleDateTimeInput
              value={scheduleAt}
              onChange={setScheduleAt}
              minValue={toLocalDateTimeInputValue(new Date())}
            />
          )}
        </ConfirmModal>
      )}

      {versionHistoryOpen && (
        <VersionHistoryModal
          loading={versionHistoryLoading}
          error={versionHistoryError}
          versions={versions}
          canRestore={canEdit}
          restoringId={restoringVersionId}
          onRestore={handleRestoreVersion}
          onClose={() => setVersionHistoryOpen(false)}
        />
      )}

      {previewOpen && (
        <div className={styles.previewOverlay} onClick={() => setPreviewOpen(false)}>
          <div className={styles.previewModal} onClick={(e) => e.stopPropagation()}>
            <button className={styles.previewCloseBtn} onClick={() => setPreviewOpen(false)}>
              <IoClose />
            </button>
            {renderCanvasFrame(styles[`canvasFrame_${device}`], { isModal: true })}
          </div>
        </div>
      )}
    </>
  );
}
